import React, { useState, useEffect, useRef } from 'react';
import { enablePush, disablePush, isPushOnThisDevice, pushBlockedReason, isInstalled, registerServiceWorker, clearOfflineData } from './lib/push';
import { canPromptInstall, onInstallAvailabilityChange, promptInstall, installHint } from './lib/install';
import axios from 'axios';
import './App.css';
import BRAND, { applyBrandTheme } from './brandConfig';
import { watchForNewBuild } from './lib/appVersion';
import { AppContext } from './context/AppContext';
import { t, currentLanguage } from './i18n';
import { fmtMoney } from './lib/format';

import Home from './screens/Home';
import RequestForm, { SERVICE_TYPES } from './screens/RequestForm';
import AccountDashboard, { PropertyDetail } from './screens/AccountDashboard';
import { PropertyForm, UnitForm, MemberForm, AccountWorkOrderForm, PropertyImport } from './screens/AccountForms';
import { LoginChoice, HomeownerLogin, VendorLogin, AdminLogin, AccountLogin, ForgotPassword, ResetPassword } from './screens/Auth';
import { HomeownerDashboard, RequestDetail, EstimateReview } from './screens/HomeownerDashboard';
import AdminDashboard from './screens/AdminDashboard';
import VendorDashboard from './screens/VendorDashboard';
import Modals from './screens/Modals';
import { PrivacyPolicy, Terms } from './screens/Legal';
import Toast from './components/Toast';
import PrintDoc from './components/PrintDoc';
import OfflineBar from './components/OfflineBar';
import UpdateBar from './components/UpdateBar';

// In dev, requests use the CRA proxy (client/package.json → "proxy") to reach
// the backend on :5001. In production the Express server serves this app, so
// API calls are same-origin (empty base URL). REACT_APP_API_URL can override.
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// The signed-in session, kept on the device.
//
// Until this existed the token lived only in React state, so *any* reload threw
// it away and dumped you back on the sign-in screen: pull-to-refresh, the back
// button, following a link out and returning — and on iOS especially, Safari
// silently discarding a background tab, which it does aggressively. Julian was
// refreshing to see new messages, so he was signing in every single time.
// Server tokens are good for 7 days; there is no reason the phone should forget
// them sooner.
const SESSION_KEY = 'qps-session';

const readSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    return saved?.token && saved?.user ? saved : null;
  } catch {
    return null;   // private mode, or a storage entry we can't read
  }
};

const screenForRole = (role) =>
  role === 'admin' ? 'screen-admin-dashboard'
  : role === 'vendor' ? 'screen-vendor-dashboard'
  : role === 'manager' ? 'screen-account-dashboard'
  : 'screen-homeowner-dashboard';

// App owns ALL state and handlers, then exposes them through AppContext so each
// screen can read exactly what it needs. The render below is just a list of
// screens — every screen shows/hides itself based on `currentScreen`.
function App() {
  // Read once, at boot. The effects below that handle reset links and /privacy
  // style paths still run afterwards and still win — someone opening a password
  // link should land on the password screen whether or not they're signed in.
  const restored = readSession();

  const [currentScreen, setCurrentScreen] = useState(
    restored ? screenForRole(restored.user.role) : 'screen-home'
  );

  // Apply the active brand's theme colors + page title once on load.
  useEffect(() => { applyBrandTheme(); }, []);

  // Auth state
  const [authToken, setAuthToken] = useState(restored?.token || null);
  const [currentUser, setCurrentUser] = useState(restored?.user || null);
  const [authForm, setAuthForm] = useState({ email: '', password: '', name: '', phone: '', mode: 'login', smsConsent: false });
  const [authError, setAuthError] = useState('');

  // Request form state
  const [formData, setFormData] = useState({
    fullName: '', phone: '', email: '', serviceAddress: '', zip: '',
    city: '', cityOther: '',
    billingSame: true, serviceType: '', description: '',
    urgency: 'Standard – 1 week', bestTime: '8-10 am',
    // Express opt-in to job texts. Starts false and must be ticked by hand —
    // carriers reject A2P campaigns whose consent box is pre-checked.
    smsConsent: false
  });

  // What the customer wants done, a line each — the leak, the gate, the hole in
  // the wall. Each line holds its own photos as FILES, because the request form
  // is public and /api/uploads needs an account: nothing is uploaded until the
  // request itself is submitted, and then it all goes in one multipart post.
  const newRequestItem = () => ({ title: '', serviceType: '', description: '', files: [] });
  const [requestItems, setRequestItems] = useState([newRequestItem()]);
  // Index of the line whose own screen is open, or null for the form itself.
  const [editingRequestItem, setEditingRequestItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dashboard state
  const [requests, setRequests] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeRequest, setActiveRequest] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [adminView, setAdminView] = useState('leads'); // 'leads' | 'clients' | 'techs' | 'ledger' | 'projects' | 'texting'
  // Inbound-text diagnostics: is the relay armed, and what has actually arrived.
  const [smsStatus, setSmsStatus] = useState({ enabled: false, events: [], loaded: false });
  const [isLoadingSms, setIsLoadingSms] = useState(false);
  // Set when the admin taps a vendor's name — swaps the Vendors list for that
  // vendor's profile (their jobs + ledger + progress reports).
  const [activeVendor, setActiveVendor] = useState(null);
  // Set when the admin taps a client — swaps the Clients list for that client's
  // own page: how to reach them, what they have been billed, what they owe.
  const [activeClient, setActiveClient] = useState(null);
  // Vendors tab layout: 'board' is the Trello-style columns-by-trade view,
  // 'list' is the original stacked cards (which support swipe-to-delete).
  const [vendorLayout, setVendorLayout] = useState('board');

  // Admin editing a technician's details. Null when the form is closed.
  const [vendorEditForm, setVendorEditForm] = useState(null);
  // The set-password link from the technician just added, kept on screen.
  const [lastVendorLink, setLastVendorLink] = useState('');
  const [isSavingVendor, setIsSavingVendor] = useState(false);

  // Admin delete-with-confirmation: { type: 'lead'|'client'|'vendor', id, label }
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Modal state
  const [modal, setModal] = useState(null); // 'estimate' | 'assign' | 'completion' | 'invoice'
  const [estimateForm, setEstimateForm] = useState({ breakdown: [{ label: '', description: '', photos: [], cost: '' }], notes: '' });
  // Set while writing an estimate for a client who has no job on file yet —
  // Create Estimate on their own page. The job is created when the estimate is
  // sent, not before, so a sheet he opens and thinks better of doesn't leave a
  // half-lead in the pipeline. Holds { client, jobTitle, serviceType }.
  const [estimateForClient, setEstimateForClient] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ lineItems: [{ description: '', qty: 1, unitCost: '', total: '' }], notes: '' });
  const [completionFiles, setCompletionFiles] = useState([]);
  const [completionNote, setCompletionNote] = useState('');
  const [assignVendorId, setAssignVendorId] = useState('');

  // Admin: manually-added lead + appointment scheduling
  //
  // A phoned-in lead carries the same LIST of jobs as one submitted from the
  // website or taken by text — same editor screen, same shape in the database.
  // Julian on the phone hears "the faucet, and the gate, and the drywall" just
  // as often as the web form does.
  const [addLeadForm, setAddLeadForm] = useState({
    fullName: '', phone: '', email: '', serviceAddress: '',
    city: '', cityOther: '', serviceType: '', description: '',
    urgency: 'Standard – 1 week', leadSource: 'Facebook', appointmentAt: '',
    items: [{ title: '', serviceType: '', description: '', photos: [] }],
  });
  // Index of the lead's line whose own screen is open, or null.
  const [editingLeadItem, setEditingLeadItem] = useState(null);
  const [appointmentInput, setAppointmentInput] = useState('');

  // Admin: a client added by hand.
  //
  // The website only ever creates a client as a by-product of somebody filing a
  // request, so the repeat customers Julian has had for years were only in the
  // app if they happened to use the form. This is him putting one in directly,
  // with the two addresses he actually needs: where the work is, and where the
  // invoice goes.
  const [addClientForm, setAddClientForm] = useState(null);
  const [isSavingClient, setIsSavingClient] = useState(false);
  // The set-password link from the last client added, kept on screen so it can
  // be texted or read out — same reason the commercial invites keep theirs.
  const [lastClientLink, setLastClientLink] = useState('');

  // Change-password modal (any logged-in user; used from the admin nav)
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  // Change the address you sign in with. Guarded by the current password,
  // because the sign-in email IS the account.
  const [emailForm, setEmailForm] = useState({ current: '', next: '' });

  // Forgot/reset password (homeowner + technician portals)
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState(''); // confirmation shown after the email is sent
  const [forgotFrom, setForgotFrom] = useState('screen-login-choice'); // login screen to return to
  const [resetToken, setResetToken] = useState(null); // token from the emailed ?reset= link
  const [resetForm, setResetForm] = useState({ password: '', confirm: '' });
  const [isResetting, setIsResetting] = useState(false);

  // An emailed reset link opens the site as /?reset=<token> — jump straight
  // to the choose-a-new-password screen and clean the token out of the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset');
    if (token) {
      setResetToken(token);
      setCurrentScreen('screen-reset-password');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('portal')) {
      // "View My Portal" email link → straight to the client sign-in screen
      setCurrentScreen('screen-homeowner-login');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Land someone straight on the screen their link names, rather than the
  // homepage. The back button has to work from there too, so popstate maps the
  // path back onto a screen.
  useEffect(() => {
    const screenForPath = () => {
      const p = window.location.pathname.replace(/\/+$/, '').toLowerCase();
      // A trailing "s" is the obvious thing to mistype in a link read off a
      // business card, so accept both spellings of the two shared ones.
      const aliases = {
        '/privacy': 'screen-privacy',
        '/terms': 'screen-terms',
        // The link Julian hands to techs. It opens on SIGN IN — most people
        // following it already have an account and are trying to get to their
        // jobs. Recruiting has its own link below.
        '/technicians': 'screen-vendor-login',
        '/technician': 'screen-vendor-login',
        // For recruiting a NEW tech: same screen, opened on Create Account.
        '/apply': 'screen-vendor-login',
        '/join': 'screen-vendor-login',
        '/portal': 'screen-homeowner-login',
        // The link Julian hands a property management company. Several
        // spellings, because this one gets read off an email and retyped.
        '/commercial': 'screen-account-login',
        '/commercial-accounts': 'screen-account-login',
        '/accounts': 'screen-account-login',
        '/manager': 'screen-account-login',
      };
      return aliases[p] || null;
    };
    const landed = screenForPath();
    if (landed) {
      setCurrentScreen(landed);
      // Only the recruiting links open on Create Account. /technicians itself
      // is the everyday "get me to my jobs" link and must open on Sign In —
      // it used to open on sign-up, which showed a returning tech a blank
      // registration form for an account they already had.
      const p = window.location.pathname.replace(/\/+$/, '').toLowerCase();
      if (landed === 'screen-vendor-login') {
        setAuthForm(prev => ({ ...prev, mode: ['/apply', '/join'].includes(p) ? 'register' : 'login' }));
      }
    }

    // Stamp the entry the app opened on, so coming back to it restores that
    // screen rather than falling through to the homepage.
    window.history.replaceState(
      { screen: landed || screenRef.current },
      '',
      window.location.pathname,
    );

    // Back undoes ONE thing: the nearest one. A sheet on top of a profile panel
    // on top of a screen is three presses, in that order — which is the only
    // arrangement that doesn't lose work somebody is in the middle of.
    //
    // Each layer got its own history entry when it opened (see the effect
    // below), so closing one here consumes that entry and nothing is re-pushed.
    const onPop = (e) => {
      const open = backStack.current;
      const closed = () => { open.closedByBack.current = true; };

      // A job's own editor, which sits on top of the Add Lead sheet.
      if (open.editingLeadItem !== null && open.editingLeadItem !== undefined) {
        open.setEditingLeadItem(null); closed(); return;
      }
      // Any bottom sheet: estimate, assign, add lead, add/edit client, invoice.
      if (open.modal || open.vendorEditForm || open.lastVendorLink) {
        open.setModal(null); open.setVendorEditForm(null); open.setLastVendorLink('');
        closed(); return;
      }
      // The full-screen panels that aren't screens of their own.
      if (open.activeVendor) { open.setActiveVendor(null); closed(); return; }
      if (open.activeClient) { open.setActiveClient(null); closed(); return; }
      if (open.activeProject) { open.setActiveProject(null); closed(); return; }

      // Nothing covering it, so this is a real screen change. The entry says
      // which screen it was; a link opened cold has only its path to go on.
      setCurrentScreen(e.state?.screen || screenForPath() || 'screen-home');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Technician partnership application (mirrors the old Google Form).
  // vendorProfile is the tech's own Vendor record; the application form shows
  // until profile.completedAt is set.
  const [vendorProfile, setVendorProfile] = useState(null);
  const [vendorAppForm, setVendorAppForm] = useState({
    address: '', yearsExperience: '',
    expertise: [], expertiseOther: '',
    skills: [], skillsOther: '',
    hasTools: '', licensed: '', insured: '',
    hasCompany: '', companyLink: '', hasCrew: '',
    hourlyRate: '', sqftRate: ''
  });
  const [vendorDocFiles, setVendorDocFiles] = useState([]);
  const [isSubmittingApp, setIsSubmittingApp] = useState(false);

  // ── Vendor Ledger + compliance ──────────────────────────────
  // The ledger tracks every vendor invoice, what's been paid against it, and
  // the live progress reports attached to it. Compliance (insurance / license
  // expiry) rides along because it gates whether a vendor can be paid.
  const [ledger, setLedger] = useState([]);
  const [ledgerVendorFilter, setLedgerVendorFilter] = useState('');
  // Which technician-portal tab is showing. The first five are the bottom tab
  // bar; 'docs' and 'settlements' are pushed from More and keep it lit.
  const [vendorView, setVendorView] = useState('jobs');
  // 'jobs' | 'estimates' | 'invoices' | 'payments' | 'more' | 'docs' | 'settlements'
  const [activeEntry, setActiveEntry] = useState(null);
  const [ledgerForm, setLedgerForm] = useState({
    _id: '', vendorId: '', requestId: '', invoiceNumber: '',
    serviceAddress: '', jobDetails: '', invoiceAmount: '', notes: '', status: 'open'
  });
  const [ledgerDocFiles, setLedgerDocFiles] = useState([]);
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: '', mode: 'Check', reference: '', note: '', overrideReason: '' });
  // Set when the server refuses a payment because the vendor's paperwork lapsed.
  const [paymentBlock, setPaymentBlock] = useState(null);
  const [progressForm, setProgressForm] = useState({ note: '', percentComplete: '' });
  const [progressFiles, setProgressFiles] = useState([]);
  const [credentialForm, setCredentialForm] = useState({
    vendorId: '', type: 'insurance', label: '', issuer: '', number: '', issuedAt: '', expiresAt: ''
  });
  const [credentialFile, setCredentialFile] = useState(null);
  const [isSavingLedger, setIsSavingLedger] = useState(false);

  // ── Projects ────────────────────────────────────────────────
  // A project is one property job made of many trades. The server shapes the
  // payload per role: the homeowner gets the statement, a tech gets only their
  // own settlement, the admin gets everything including profit.
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [projectForm, setProjectForm] = useState({ _id: '', title: '', homeownerId: '', clientName: '', address: '', notes: '' });
  const [lineForm, setLineForm] = useState({ _id: '', trade: '', invoiceNumber: '', invoiceUrl: '', amount: '', vendorId: '', techCost: '' });
  const [clientPayForm, setClientPayForm] = useState({ lineId: '', amount: '', date: '', method: 'Check', reference: '' });
  const [expenseForm, setExpenseForm] = useState({ lineId: '', description: '', supplier: '', amount: '', paymentDetails: '', chargeToVendorId: '' });
  const [receiptFile, setReceiptFile] = useState(null);
  const [estimateForm2, setEstimateForm2] = useState({ trade: '', serviceAddress: '', notes: '', items: [{ name: '', description: '', kind: 'labor', qty: 1, unitCost: '', photos: [] }] });
  const [isSavingProject, setIsSavingProject] = useState(false);

  // ── In-app messages ─────────────────────────────────────────
  // One thread per job, shared by the homeowner, the assigned tech and QPS.
  const [thread, setThread] = useState([]);
  const [threadFor, setThreadFor] = useState(null);   // the request being discussed
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageFiles, setMessageFiles] = useState([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});  // { requestId: count }

  const [toast, setToast] = useState('');
  const [homeownerTab, setHomeownerTab] = useState('requests');
  const [vendorTab, setVendorTab] = useState('active');

  // ── Helpers ──────────────────────────────────────────────

  // Screens are state, not routes — except the handful below, which are the
  // ones that get sent to someone who isn't on the site yet:
  //   /privacy, /terms  — carriers paste these into a browser while reviewing
  //                       our A2P text-messaging registration.
  //   /technicians      — the link Julian sends a tech to sign up.
  //   /portal           — the link a customer uses to reach their portal.
  // Express already serves index.html for any path, so the work is all here:
  // put the path in the address bar on the way in, restore / on the way out.
  // What the phone's Back button is looking at right now. Kept in refs so the
  // popstate listener — registered once, on mount — reads today's values
  // instead of the ones that existed when it was wired up.
  const screenRef = useRef(currentScreen);
  screenRef.current = currentScreen;
  const backStack = useRef({});
  // How many sheets/panels were open last time round, and whether the one that
  // just closed was closed BY Back — which means its history entry is already
  // gone and must not be wound off a second time.
  const overlayDepthRef = useRef(0);
  const closedByBack = useRef(false);

  const SCREEN_PATHS = {
    'screen-privacy': '/privacy',
    'screen-terms': '/terms',
    'screen-vendor-login': '/technicians',
    'screen-homeowner-login': '/portal',
    'screen-account-login': '/commercial',
  };

  // Every screen change is a history entry now, not just the five with public
  // URLs. Before this, Back from anywhere inside the app — a job, a property, a
  // technician's profile — left the site altogether and landed on whatever page
  // was open before it, because nothing had been pushed to go back TO.
  //
  // Screens without a public URL keep the plain "/" address; the entry still
  // carries which screen it was in history.state, which is what Back reads.
  const goTo = (screen) => {
    if (screen !== screenRef.current) {
      window.history.pushState({ screen }, '', SCREEN_PATHS[screen] || '/');
    }
    setCurrentScreen(screen);
  };

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${authToken}` } });

  // Mirror the session to the device whenever it changes. Covers signing in,
  // signing out and the token being cleared underneath us — one place, so no
  // future code path can forget to keep the two in step.
  useEffect(() => {
    try {
      if (authToken && currentUser) {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ token: authToken, user: currentUser }));
      } else {
        localStorage.removeItem(SESSION_KEY);
      }
    } catch { /* private mode: the session just won't outlive the tab */ }
  }, [authToken, currentUser]);

  // A restored token is up to 7 days old and may have expired. Without this the
  // app would look signed in while every request quietly failed — a worse state
  // to be in than the sign-in screen. One 401 from the API and we clear out.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      res => res,
      err => {
        // Only a rejected *token* signs you out. A 401 from the login form
        // itself is a wrong password, and must leave the form alone.
        const url = err?.config?.url || '';
        const isLoginAttempt = url.includes('/api/auth/login') || url.includes('/api/auth/register');
        if (err?.response?.status === 401 && !isLoginAttempt) {
          setAuthToken(null);
          setCurrentUser(null);
          // Leave the dashboard too. Clearing the token while staying on it
          // would show an empty portal whose every button fails — worse than
          // the sign-in screen, because it looks broken rather than expired.
          setCurrentScreen('screen-home');
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const logout = () => {
    // Wipe cached API responses too, or the next person on a shared phone can
    // page through the last user's jobs with no signal and no password.
    clearOfflineData();
    setAuthToken(null);
    setCurrentUser(null);
    setRequests([]);
    setVendors([]);
    setLedger([]);
    setProjects([]);
    setEstimates([]);
    setActiveProject(null);
    setVendorProfile(null);
    goTo('screen-home');
  };

  // ── Data Loaders ─────────────────────────────────────────

  const loadHomeownerRequests = async () => {
    try {
      const res = await axios.get('/api/requests', authHeaders());
      setRequests(res.data);
    } catch { /* silently fail */ }
    try {
      const pRes = await axios.get('/api/projects', authHeaders());
      setProjects(pRes.data);
    } catch { /* silently fail */ }
  };

  const loadAdminData = async () => {
    try {
      const [rRes, vRes, cRes, lRes] = await Promise.all([
        axios.get('/api/requests', authHeaders()),
        axios.get('/api/vendors', authHeaders()),
        axios.get('/api/auth/clients', authHeaders()),
        axios.get('/api/ledger', authHeaders())
      ]);
      setRequests(rRes.data);
      setVendors(vRes.data);
      setClients(cRes.data);
      setLedger(lRes.data);
    } catch { /* silently fail */ }
    try {
      const [pRes, eRes] = await Promise.all([
        axios.get('/api/projects', authHeaders()),
        axios.get('/api/estimates', authHeaders()),
      ]);
      setProjects(pRes.data);
      setEstimates(eRes.data);
    } catch { /* silently fail */ }
  };

  const loadVendorJobs = async () => {
    // Fetch independently — a failure on one shouldn't blank the others.
    try {
      const rRes = await axios.get('/api/requests', authHeaders());
      setRequests(rRes.data);
    } catch { /* silently fail */ }
    try {
      const meRes = await axios.get('/api/vendors/me', authHeaders());
      setVendorProfile(meRes.data);
    } catch { /* silently fail */ }
    try {
      const lRes = await axios.get('/api/ledger', authHeaders());
      setLedger(lRes.data);
    } catch { /* silently fail */ }
    try {
      const [pRes, eRes] = await Promise.all([
        axios.get('/api/projects', authHeaders()),
        axios.get('/api/estimates', authHeaders()),
      ]);
      setProjects(pRes.data);
      setEstimates(eRes.data);
    } catch { /* silently fail */ }
  };

  const loadUnread = async () => {
    try {
      const res = await axios.get('/api/messages/unread', authHeaders());
      setUnreadCounts(res.data);
    } catch { /* silently fail */ }
  };

  // Re-pulls whichever dataset the current user is looking at, so a ledger
  // action refreshes the right dashboard without the caller knowing the role.
  const refreshData = async () => {
    if (currentUser?.role === 'admin') await loadAdminData();
    else if (currentUser?.role === 'vendor') await loadVendorJobs();
    else if (currentUser?.role === 'manager') await loadAccountData();
  };

  // Opens the admin's technician sheet. With a vendor it corrects theirs; with
  // nothing it adds one — a tech Julian has used for years who was never in the
  // app because he was never going to stop mid-job and fill in a web form.
  const openVendorAdd = () => {
    setVendorEditForm({
      _id: '', contactName: '', businessName: '', occupation: '', phone: '', email: '',
      address: '', homeAddress: '', yearsExperience: '',
      hasTools: '', licensed: '', insured: '', hasCrew: '',
      hourlyRate: '', sqftRate: '',
      // Trades are the board columns. A tech added without one lands in "No
      // trade on file", which is where techs go to be forgotten.
      expertise: [], expertiseOther: '', sendInvite: false,
    });
    setLastVendorLink('');
  };

  // Opens the admin's edit form pre-filled from the vendor record.
  const openVendorEdit = (v) => {
    const p = v.profile || {};
    setLastVendorLink('');
    setVendorEditForm({
      _id: v._id,
      contactName: v.userId?.name || '',
      businessName: v.businessName || '',
      occupation: p.occupation || v.specialty || '',
      phone: v.phone || v.userId?.phone || '',
      email: v.email || v.userId?.email || '',
      address: p.address || '',
      homeAddress: p.homeAddress || '',
      yearsExperience: p.yearsExperience || '',
      hasTools: p.hasTools || '',
      licensed: p.licensed || '',
      insured: p.insured || '',
      hasCrew: p.hasCrew || '',
      hourlyRate: p.hourlyRate || '',
      sqftRate: p.sqftRate || '',
    });
  };

  const handleSaveVendorEdit = async () => {
    const f = vendorEditForm;
    if (!f?.businessName?.trim()) { alert('A business name is required.'); return; }
    if (!f._id && !f.phone?.trim() && !f.email?.trim()) {
      alert('A phone number or an email address is required.'); return;
    }
    setIsSavingVendor(true);
    try {
      const { _id, ...fields } = f;
      if (_id) {
        const res = await axios.patch(`/api/vendors/${_id}`, fields, authHeaders());
        setVendors(prev => prev.map(v => (v._id === _id ? res.data : v)));
        // Keep the open profile panel in step with what was just saved.
        setActiveVendor(prev => (prev && prev._id === _id ? res.data : prev));
        setVendorEditForm(null);
        showToast('Technician details updated');
      } else {
        const res = await axios.post('/api/vendors', fields, authHeaders());
        showToast(res.data?.invited ? 'Technician added and invite sent' : 'Technician added');
        await loadAdminData();
        setVendorEditForm(null);
        // Their way into the portal, kept on screen to text or read out — the
        // same reason the client and commercial invites keep theirs.
        if (res.data?.link) setLastVendorLink(res.data.link);
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingVendor(false); }
  };

  // The eye on a vendor card. Optimistic — the toggle should feel instant, and
  // the reload right after puts it back if the server disagreed.
  const handleToggleWatch = async (vendor) => {
    const next = !vendor.watched;
    setVendors(prev => prev.map(v => (v._id === vendor._id ? { ...v, watched: next } : v)));
    try {
      await axios.patch(`/api/vendors/${vendor._id}/watch`, { watched: next }, authHeaders());
      showToast(next ? `Watching ${vendor.businessName}` : `Stopped watching ${vendor.businessName}`);
    } catch (err) {
      setVendors(prev => prev.map(v => (v._id === vendor._id ? { ...v, watched: !next } : v)));
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Moving a vendor card between columns on the admin board. The columns are
  // the vendor's trades, so this drops the trade the card came from and adds
  // the one it landed on. A tech listed under two trades shows up as two
  // cards; moving one only affects that trade.
  const handleMoveVendorTrade = async (vendor, from, to) => {
    if (!to || from === to) return;
    try {
      const res = await axios.patch(
        `/api/vendors/${vendor._id}/trades`, { from, to }, authHeaders()
      );
      setVendors(prev => prev.map(v => (
        v._id === vendor._id
          ? { ...v, profile: { ...(v.profile || {}), expertise: res.data.expertise } }
          : v
      )));
      showToast(`${vendor.businessName} moved to ${to}`);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
      await refreshData();   // put the board back to whatever the server thinks
    }
  };

  // "Copy link" on a card produces /#/vendor/<id>. There's no router in this
  // app — screens are state — so the link is a hash the admin dashboard reads
  // once the vendor list has loaded. Anyone not signed in as admin just lands
  // on the homepage, which is the right outcome for a link that leaked.
  useEffect(() => {
    const match = /^#\/vendor\/([a-f0-9]{24})$/i.exec(window.location.hash);
    if (!match || currentUser?.role !== 'admin' || vendors.length === 0) return;
    const vendor = vendors.find(v => v._id === match[1]);
    window.history.replaceState(null, '', window.location.pathname);  // don't re-fire
    if (!vendor) { showToast('That vendor no longer exists'); return; }
    setAdminView('techs');
    setActiveVendor(vendor);
  }, [vendors, currentUser]); // eslint-disable-line

  useEffect(() => {
    if (!authToken) return;
    if (currentScreen === 'screen-homeowner-dashboard') loadHomeownerRequests();
    if (currentScreen === 'screen-admin-dashboard') { loadAdminData(); loadAdminAccounts(); }
    if (currentScreen === 'screen-vendor-dashboard') loadVendorJobs();
    if (currentScreen === 'screen-account-dashboard') loadAccountData();
    if (currentScreen.endsWith('-dashboard')) loadUnread();
  }, [currentScreen, authToken]); // eslint-disable-line

  // ── Auth ─────────────────────────────────────────────────

  const handleAuthInputChange = (e) => {
    const { name, type, value, checked } = e.target;
    setAuthForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setAuthError('');
  };

  // `intent` forces sign-in or sign-up regardless of the shared form state.
  //
  // The Admin screen has no Sign In / Create Account toggle — it only ever
  // shows a Sign In button — but it used to branch on `authForm.mode` like
  // every other screen. That mode is shared across all three portals, so after
  // a visit to the technician page (which opened on Create Account) the admin
  // button silently submitted a REGISTRATION. Julian was locked out of his own
  // portal by "Email already in use", which is a sign-up error appearing under
  // a button that says Sign In. A screen with no way to change the mode must
  // never be at the mercy of it.
  const handleAuthSubmit = async (role, intent) => {
    setAuthError('');
    try {
      let res;
      if ((intent || authForm.mode) === 'login') {
        res = await axios.post('/api/auth/login', { email: authForm.email, password: authForm.password });
      } else {
        res = await axios.post('/api/auth/register', {
          email: authForm.email, password: authForm.password,
          name: authForm.name, phone: authForm.phone, role,
          smsConsent: authForm.smsConsent,
          // Only the Commercial Accounts form collects this; registering as a
          // manager creates the company and makes them its owner.
          companyName: authForm.companyName,
        });
      }
      const { token, user } = res.data;
      const wasRegister = (intent || authForm.mode) === 'register';
      setAuthToken(token);
      setCurrentUser(user);
      setAuthForm({ email: '', password: '', name: '', phone: '', companyName: '', mode: 'login', smsConsent: false });

      goTo(screenForRole(user.role));

      showToast((wasRegister ? t('toast.welcome') : t('toast.welcomeBack')).replace('{name}', user.name || ''));
    } catch (err) {
      setAuthError(err.response?.data?.message || t('err.generic'));
    }
  };

  // Opens the forgot-password screen from a portal's sign-in card, remembering
  // which one so "Back to sign in" returns there.
  const openForgotPassword = (fromScreen) => {
    setForgotFrom(fromScreen);
    setForgotEmail(authForm.email);
    setForgotStatus('');
    setAuthError('');
    goTo('screen-forgot-password');
  };

  const handleForgotPassword = async () => {
    setAuthError('');
    if (!forgotEmail.trim()) { setAuthError(t('err.enterEmail')); return; }
    try {
      await axios.post('/api/auth/forgot-password', { email: forgotEmail });
      setForgotStatus(t('forgot.sent'));
    } catch (err) {
      setAuthError(err.response?.data?.message || t('err.generic'));
    }
  };

  const handleResetPassword = async () => {
    setAuthError('');
    if (resetForm.password.length < 8) { setAuthError(t('err.pw8')); return; }
    if (resetForm.password !== resetForm.confirm) { setAuthError(t('err.pwMatch')); return; }
    setIsResetting(true);
    try {
      await axios.post('/api/auth/reset-password', { token: resetToken, password: resetForm.password });
      setResetForm({ password: '', confirm: '' });
      setResetToken(null);
      showToast(t('toast.pwUpdated'));
      goTo('screen-login-choice');
    } catch (err) {
      setAuthError(err.response?.data?.message || t('err.generic'));
    } finally {
      setIsResetting(false);
    }
  };

  // ── Request Form ──────────────────────────────────────────

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // A line is real once it says something. An empty row someone added and
    // never filled in is dropped rather than argued with.
    const lines = requestItems.filter(it => (it.title || '').trim() || (it.description || '').trim() || (it.files || []).length);
    if (lines.length === 0) { alert(t('req.needItem')); return; }
    if (lines.some(it => !(it.description || '').trim() || !it.serviceType)) { alert(t('req.itemIncomplete')); return; }
    const files = lines.flatMap(it => it.files || []);
    if (files.length === 0) { alert(t('req.needPhoto')); return; }
    // 25 is multer's limit on the route. Caught here rather than at the server,
    // where the 26th photo would cost them everything they typed.
    if (files.length > 25) { alert(t('req.max25Total')); return; }

    setIsSubmitting(true);
    try {
      // Resolve the city + classify the lead as Primary vs Outside Area.
      const isOther = formData.city === 'Other';
      const finalCity = isOther ? formData.cityOther : formData.city;
      const areaTag = (!isOther && BRAND.serviceArea.primaryCities.includes(formData.city))
        ? 'Primary Area'
        : 'Outside Area';

      const fd = new FormData();
      const base = { ...formData, city: finalCity, areaTag, leadSource: 'Website' };
      delete base.cityOther;
      // serviceType and description are rolled up from the lines by the server
      // (server/requestItems.js), so the form no longer sends its own.
      delete base.serviceType;
      delete base.description;
      const data = currentUser
        ? { ...base, fullName: currentUser.name, email: currentUser.email, phone: currentUser.phone }
        : base;
      Object.entries(data).forEach(([k, v]) => fd.append(k, v));

      // Files can't nest inside a multipart field, so every photo goes up in
      // line order under `photos` and a count per line says where each line's
      // photos stop. The server slices them back apart.
      fd.append('items', JSON.stringify(lines.map(it => ({
        title: (it.title || '').trim(),
        serviceType: it.serviceType,
        description: (it.description || '').trim(),
      }))));
      fd.append('itemPhotoCounts', JSON.stringify(lines.map(it => (it.files || []).length)));
      files.forEach(f => fd.append('photos', f));

      const headers = { 'Content-Type': 'multipart/form-data' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      await axios.post('/api/requests', fd, { headers });
      showToast(t(currentUser ? 'toast.requestSubmitted' : 'toast.requestSubmittedPortal'));
      setRequestItems([newRequestItem()]);
      setEditingRequestItem(null);
      setFormData({ fullName: '', phone: '', email: '', serviceAddress: '', zip: '', city: '', cityOther: '', billingSame: true, serviceType: '', description: '', urgency: 'Standard – 1 week', bestTime: '8-10 am', smsConsent: false });

      if (currentUser?.role === 'homeowner') {
        await loadHomeownerRequests();
        goTo('screen-homeowner-dashboard');
      } else {
        goTo('screen-home');
      }
    } catch (err) {
      alert('Error submitting request: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Photos picked on a lead's line, uploaded as they are chosen because Julian
  // is signed in — the line then carries Cloudinary URLs and the lead itself
  // posts as plain JSON. Same route the technician's estimate lines use.
  const uploadPhotos = async (files) => {
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await axios.post('/api/uploads', fd, {
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.message) showToast(res.data.message);
      return res.data?.urls || [];
    } catch (err) {
      alert('Those photos could not be uploaded: ' + (err.response?.data?.message || err.message));
      return [];
    }
  };

  // Same route as uploadPhotos, but keeps the filename.
  //
  // A photo is identified by looking at it; a document is identified by being
  // called "move_out_inspection.pdf". uploadPhotos returns bare URLs because
  // that is all an estimate line needs — this returns { url, name } because a
  // Cloudinary URL ends in a random id and nobody can tell what it is.
  const uploadDocuments = async (files, meta = {}) => {
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await axios.post('/api/uploads', fd, {
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.message) showToast(res.data.message);
      return (res.data?.files || []).map(f => ({ ...f, ...meta }));
    } catch (err) {
      alert('That file could not be uploaded: ' + (err.response?.data?.message || err.message));
      return [];
    }
  };

  // The job whose paperwork is open, as { request, kind } — 'work-order' or
  // 'estimate'. Null when nothing is being looked at.
  const [printDoc, setPrintDoc] = useState(null);
  // True once the server is serving a newer build than this tab is running —
  // see lib/appVersion.js. The bar it draws is the only thing that reloads,
  // and only when somebody presses it.
  const [newBuild, setNewBuild] = useState(false);
  useEffect(() => watchForNewBuild(() => setNewBuild(true)), []);
  const [isRaisingWorkOrder, setIsRaisingWorkOrder] = useState(false);

  // Raise the work order if this job hasn't got one, then open it. Julian's
  // "Create Work Order" and "View & Print PDF" are one motion in practice: he
  // wants the sheet, and whether it already exists is our problem, not his.
  //
  // The number is minted once, server-side — opening the sheet again never
  // makes a second one.
  const openWorkOrder = async (request) => {
    if (request.workOrder?.number) { setPrintDoc({ request, kind: 'work-order' }); return; }
    setIsRaisingWorkOrder(true);
    try {
      const res = await axios.post(`/api/requests/${request._id}/work-order`, {}, authHeaders());
      setRequests(prev => prev.map(r => (r._id === res.data._id ? { ...r, ...res.data } : r)));
      setPrintDoc({ request: { ...request, ...res.data }, kind: 'work-order' });
      showToast(`Work order ${res.data.workOrder?.number} raised`);
      await refreshAdminAccountDetail();
    } catch (err) {
      alert('Error raising the work order: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsRaisingWorkOrder(false);
    }
  };

  // ── Estimate (Admin) ──────────────────────────────────────

  // Which job on the request Julian is editing the note of while he prices it,
  // and what he has typed: { index, title, description }. Null when the panel
  // is just showing what the customer wrote.
  const [noteEdit, setNoteEdit] = useState(null);
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Saves a corrected note back onto the job. The server keeps the customer's
  // original wording (descriptionOriginal) and re-rolls the request's summary,
  // so the lead card and the estimate can't drift apart.
  const handleSaveNote = async () => {
    if (!noteEdit || !activeRequest) return;
    setIsSavingNote(true);
    try {
      const res = await axios.patch(
        `/api/requests/${activeRequest._id}/notes`,
        { index: noteEdit.index, title: noteEdit.title, description: noteEdit.description },
        authHeaders()
      );
      setActiveRequest(res.data);
      setRequests(prev => prev.map(r => (r._id === res.data._id ? res.data : r)));
      // The estimate row was labelled from this job, so keep the label he is
      // about to send in step with the note he just fixed — but only that row,
      // and only if he hasn't already renamed it himself.
      setEstimateForm(p => {
        const b = [...p.breakdown];
        const was = activeRequest.items?.[noteEdit.index];
        const row = b[noteEdit.index];
        if (row && was && (row.label === was.title || !row.label)) {
          b[noteEdit.index] = { ...row, label: res.data.items?.[noteEdit.index]?.title || row.label };
        }
        return { ...p, breakdown: b };
      });
      setNoteEdit(null);
      showToast('Note updated');
    } catch (err) {
      alert('Error saving note: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSavingNote(false);
    }
  };

  const estimateTotal = estimateForm.breakdown.reduce((sum, r) => sum + (parseFloat(r.cost) || 0), 0);

  const handleSubmitEstimate = async () => {
    try {
      // Started from a client's page: log the job first, from their own
      // record, then price it. Their name, number, email and address come off
      // the client rather than out of Julian's memory, and the job carries
      // their account so it lands back on their page.
      // A line with a price and no name is dropped on the way out (a nameless
      // line cannot print), so say so rather than letting money vanish
      // quietly between the sheet and the document.
      const nameless = estimateForm.breakdown.find(r => !r.label?.trim() && (parseFloat(r.cost) || 0) > 0);
      if (nameless) { alert('One of the lines has a price but no name. Name it, or remove it.'); return; }

      let requestId = activeRequest?._id;
      if (estimateForClient) {
        const { client } = estimateForClient;
        // The job takes its name and its jobs from the estimate itself. Asking
        // "what is the work?" above the lines was asking the same question
        // twice: the first line already says DRYWALL, and typing it out again
        // before you can price anything is a field in the way of the work.
        const named = estimateForm.breakdown.filter(r => r.label?.trim());
        if (!named.length) { alert('Add a line item first — its name is what the job is called.'); return; }
        const where = [client.address, client.address2].filter(Boolean).join(', ');
        const res = await axios.post('/api/requests/manual', {
          fullName: client.name,
          phone: client.phone || client.phone2 || '',
          email: client.email || '',
          serviceAddress: where || client.city || 'Address pending',
          city: client.city || '',
          areaTag: BRAND.serviceArea.primaryCities.includes(client.city) ? 'Primary Area' : 'Outside Area',
          leadSource: 'Existing client',
          homeownerId: client._id,
          serviceType: 'Other',
          // A job per line, carrying the scope and the photos written on it —
          // so the lead, the work order and the estimate all say the same
          // thing without any of it being typed twice.
          items: named.map(r => ({
            title: r.label.trim(),
            serviceType: 'Other',
            description: (r.description || '').trim(),
            photos: r.photos || [],
          })),
        }, authHeaders());
        requestId = res.data._id;
      }
      await axios.post(`/api/requests/${requestId}/estimate`, {
        amount: estimateTotal,
        // Blank qty/rate are left off entirely rather than saved as empty
        // strings — a row with no workings should look like one.
        breakdown: estimateForm.breakdown.filter(r => r.label).map(r => ({
          label: r.label,
          // The scope, and the photos of what it covers. Both ride on the line
          // rather than the document, so a line moved or dropped takes its own
          // words and pictures with it.
          ...(r.description?.trim() ? { description: r.description.trim() } : {}),
          ...((r.photos || []).length ? { photos: r.photos } : {}),
          cost: parseFloat(r.cost) || 0,
          ...(r.qty !== '' && r.qty != null && !isNaN(parseFloat(r.qty)) ? { qty: parseFloat(r.qty) } : {}),
          ...(r.rate !== '' && r.rate != null && !isNaN(parseFloat(r.rate)) ? { rate: parseFloat(r.rate) } : {}),
        })),
        notes: estimateForm.notes
      }, authHeaders());
      showToast(estimateForClient
        ? 'Job created and estimate sent'
        : activeRequest?.accountId ? 'Estimate sent to the company' : 'Estimate sent to homeowner!');
      setModal(null);
      setEstimateForm({ breakdown: [{ label: '', description: '', photos: [], cost: '' }], notes: '' });
      setEstimateForClient(null);
      setNoteEdit(null);
      await loadAdminData();
      // A property's page reads its jobs out of the account detail, so a price
      // saved from there has to land in the same copy or the row behind the
      // sheet still says no estimate.
      await refreshAdminAccountDetail();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Julian approving an estimate the customer agreed to in person or on the
  // phone, then assigning it — which is one motion in real life, so the assign
  // sheet opens on the way out rather than making him find the card again.
  const handleAdminApproveEstimate = async (request, approved) => {
    const who = request.homeownerId?.name || request.fullName;
    const ok = window.confirm(approved
      ? `Approve this estimate on behalf of ${who}?\n\nTheir job moves to Approved and their portal will show that we approved it for them.`
      : `Mark this estimate as declined by ${who}?`);
    if (!ok) return;
    try {
      const res = await axios.post(`/api/requests/${request._id}/approve-estimate`, { approved }, authHeaders());
      showToast(approved ? 'Estimate approved' : 'Estimate marked declined');
      await loadAdminData();
      if (approved) {
        setActiveRequest(res.data);
        setAssignVendorId('');
        setModal('assign');
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Opens the estimate sheet on a job that already has one, so it can be read
  // and corrected rather than written from scratch.
  const openEstimate = (request) => {
    setActiveRequest(request);
    setNoteEdit(null);
    const rows = request.estimate?.breakdown?.length
      ? request.estimate.breakdown.map(r => ({
          label: r.label || '',
          description: r.description || '',
          photos: r.photos || [],
          cost: r.cost != null ? String(r.cost) : '',
          qty: r.qty != null ? String(r.qty) : '',
          rate: r.rate != null ? String(r.rate) : '',
        }))
      // No estimate yet: one priced row per job the customer listed, already
      // labelled and described in their own words, with their own photos on
      // it — the scope starts as what they asked for and gets priced, rather
      // than being typed out a second time.
      : (request.items?.length
          ? request.items.map(it => ({
              label: it.title || it.serviceType || '',
              description: it.description || '',
              photos: it.photos || [],
              cost: '',
            }))
          : [{ label: '', description: '', photos: [], cost: '' }]);
    setEstimateForm({ breakdown: rows, notes: request.estimate?.notes || '' });
    setEstimateForClient(null);
    setModal('estimate');
  };

  // Create Estimate, on a client's own page. An estimate here is a price on a
  // job, so there has to be a job — but making Julian log a lead first and
  // price it second is two screens for one act, and he is standing in someone's
  // kitchen. So the same sheet asks what the work is, and sending it creates
  // the job and the estimate together, already tied to this client.
  const openEstimateForClient = (client) => {
    setActiveRequest(null);
    setNoteEdit(null);
    setEstimateForm({ breakdown: [{ label: '', description: '', photos: [], cost: '' }], notes: '' });
    setEstimateForClient({ client });
    setModal('estimate');
  };

  const handleAssignVendor = async () => {
    if (!assignVendorId) return;
    try {
      await axios.post(`/api/requests/${activeRequest._id}/assign`, { vendorId: assignVendorId }, authHeaders());
      showToast('Vendor assigned!');
      setModal(null);
      setAssignVendorId('');
      await loadAdminData();
      // The same job may be on screen as a row on a property's page, which
      // reads its jobs out of the open account rather than off the board.
      await refreshAdminAccountDetail();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleApproveInvoice = async (reqId) => {
    try {
      await axios.post(`/api/requests/${reqId}/approve-invoice`, {}, authHeaders());
      showToast('Invoice approved — funds marked as held!');
      await loadAdminData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Admin manually logs a lead (Facebook, Nextdoor, referral, phone, etc.)
  const handleAddLead = async () => {
    if (!addLeadForm.fullName || !addLeadForm.phone) { alert('Name and phone are required.'); return; }
    try {
      const isOther = addLeadForm.city === 'Other';
      const finalCity = isOther ? addLeadForm.cityOther : addLeadForm.city;
      const areaTag = (!isOther && BRAND.serviceArea.primaryCities.includes(addLeadForm.city))
        ? 'Primary Area'
        : 'Outside Area';
      // Empty rows he opened and never filled in are dropped rather than
      // argued with — the same rule the customer's own form follows.
      const items = (addLeadForm.items || [])
        .filter(it => (it.title || '').trim() || (it.description || '').trim() || (it.photos || []).length)
        .map(it => ({
          title: (it.title || '').trim(),
          // Falls back to the lead's own Service Type, so a line he typed in a
          // hurry still lands in the right trade column.
          serviceType: it.serviceType || addLeadForm.serviceType,
          description: (it.description || '').trim(),
          photos: it.photos || [],
        }));
      const payload = { ...addLeadForm, city: finalCity, areaTag, items };
      delete payload.cityOther;
      await axios.post('/api/requests/manual', payload, authHeaders());
      showToast('Lead added!');
      setModal(null);
      setEditingLeadItem(null);
      setAddLeadForm({ fullName: '', phone: '', email: '', serviceAddress: '', city: '', cityOther: '', serviceType: '', description: '', urgency: 'Standard – 1 week', leadSource: 'Facebook', appointmentAt: '', items: [{ title: '', serviceType: '', description: '', photos: [] }] });
      await loadAdminData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // One sheet, both jobs. Pass a client to correct them, nothing to add one —
  // the fields are identical, and a second near-identical form is a second
  // place for the two to drift apart.
  const openClientForm = (client) => {
    // Blank billing on the record means "bill to the service address" — the
    // switch is the same fact read the other way round. When they match, the
    // one address the form shows under BILLING ADDRESS is the service address
    // wearing the other hat, so it is seeded from it and copied back on save.
    const different = Boolean(client?.billingAddress);
    setAddClientForm(client ? {
      _id: client._id,
      name: client.name || '',
      phone: client.phone || '', phone2: client.phone2 || '', email: client.email || '',
      address: client.address || '', address2: client.address2 || '',
      city: client.city || '', state: client.state || '', zip: client.zip || '',
      billingSameAsService: !different,
      billingAddress: different ? client.billingAddress : (client.address || ''),
      billingAddress2: different ? (client.billingAddress2 || '') : (client.address2 || ''),
      billingCity: different ? (client.billingCity || '') : (client.city || ''),
      billingState: different ? (client.billingState || '') : (client.state || ''),
      billingZip: different ? (client.billingZip || '') : (client.zip || ''),
      notes: client.notes || '', sendInvite: false,
    } : {
      name: '', phone: '', phone2: '', email: '',
      address: '', address2: '', city: '', state: '', zip: '',
      billingSameAsService: true,
      billingAddress: '', billingAddress2: '', billingCity: '', billingState: '', billingZip: '',
      notes: '', sendInvite: false,
    });
    setLastClientLink('');
    setModal('addClient');
  };
  const openAddClientForm = () => openClientForm(null);

  // The + on a client's own page. A repeat customer rings about the next job
  // while you are looking at their record, so the lead starts with everything
  // that record already knows and Julian types only what is new.
  const openLeadForClient = (client) => {
    setAddLeadForm({
      fullName: client.name || '', phone: client.phone || '', email: client.email || '',
      serviceAddress: [client.address, client.address2].filter(Boolean).join(', '),
      // The lead form's city is a dropdown of the service area; a client
      // outside it (most of them) lands on Other with the city written out.
      city: BRAND.serviceArea.primaryCities.includes(client.city) ? client.city : (client.city ? 'Other' : ''),
      cityOther: BRAND.serviceArea.primaryCities.includes(client.city) ? '' : (client.city || ''),
      serviceType: '', description: '', urgency: 'Standard – 1 week',
      leadSource: 'Phone/Text', appointmentAt: '',
      items: [{ title: '', serviceType: '', description: '', photos: [] }],
    });
    setModal('addLead');
  };

  // Admin adds a client directly, rather than waiting for them to file a
  // request — or corrects one already on file.
  const handleAddClient = async () => {
    const f = addClientForm || {};
    if (!f.name?.trim()) { alert('A name is required.'); return; }
    if (!f.phone?.trim() && !f.email?.trim()) { alert('A phone number or an email address is required.'); return; }
    setIsSavingClient(true);
    try {
      const { _id, ...fields } = f;
      // With the switch off there is one address on screen, sitting under
      // BILLING ADDRESS, and it is where the work is as much as where the bill
      // goes. It is stored as the service address; the server blanks the
      // billing one, which is how "same" is written down here.
      if (fields.billingSameAsService !== false) {
        fields.address = fields.billingAddress;
        fields.address2 = fields.billingAddress2;
        fields.city = fields.billingCity;
        fields.state = fields.billingState;
        fields.zip = fields.billingZip;
      }
      const res = _id
        ? await axios.patch(`/api/auth/clients/${_id}`, fields, authHeaders())
        : await axios.post('/api/auth/clients', fields, authHeaders());
      showToast(res.data?.invited
        ? (_id ? 'Client updated and invite sent' : 'Client added and invite sent')
        : (_id ? 'Client updated' : 'Client added'));
      await loadAdminData();
      // Stay on the screen when there is a login link worth passing on —
      // editing only returns one when an email was just put on or changed,
      // which is precisely when they can be let into a portal at last. It
      // reopens on the saved record, so the link sits under the client it
      // belongs to and a second Done doesn't add them twice.
      if (res.data?.link) {
        openClientForm(res.data.client);
        setLastClientLink(res.data.link);
      } else {
        setAddClientForm(null);
        setModal(null);
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSavingClient(false);
    }
  };

  // Admin sets/updates the in-person estimate appointment for a lead
  const handleSetAppointment = async () => {
    try {
      await axios.post(`/api/requests/${activeRequest._id}/appointment`, { appointmentAt: appointmentInput || null }, authHeaders());
      showToast('Appointment saved!');
      setModal(null);
      await loadAdminData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Technician submits the partnership application
  // Runs after the admin taps Delete in the Confirm Deletion dialog.
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const { type, id } = confirmDelete;
    try {
      if (type === 'lead' || type === 'service request') {
        // The same record under two names: it is a lead on the pipeline board
        // and a service request at a property. Deleting takes the job and
        // everything filed on it — estimate, work order, photos.
        await axios.delete(`/api/requests/${id}`, authHeaders());
        setRequests(prev => prev.filter(r => r._id !== id));
        if (type === 'service request') await refreshAdminAccountDetail();
      } else if (type === 'client') {
        await axios.delete(`/api/auth/clients/${id}`, authHeaders());
        setClients(prev => prev.filter(c => c._id !== id));
      } else if (type === 'vendor') {
        await axios.delete(`/api/vendors/${id}`, authHeaders());
        setVendors(prev => prev.filter(v => v._id !== id));
      } else if (type === 'estimate') {
        const res = await axios.delete(`/api/requests/${id}/estimate`, authHeaders());
        setRequests(prev => prev.map(r => (r._id === id ? { ...r, ...res.data, estimate: null } : r)));
        setActiveRequest(prev => (prev?._id === id ? { ...prev, ...res.data, estimate: null } : prev));
        await refreshAdminAccountDetail();
      } else if (type === 'invoice') {
        // The whole bill, by its number — an invoice is every line raised
        // under it, and half a bill is not a thing anyone asked for.
        await axios.delete(`/api/projects/${id}/invoice/${encodeURIComponent(confirmDelete.number)}`, authHeaders());
        await refreshProjects();
      } else if (type === 'work order') {
        // The job stays; only the sheet raised against it goes. The number is
        // not reused — see the route.
        const res = await axios.delete(`/api/requests/${id}/work-order`, authHeaders());
        // `workOrder: null` last, and on purpose: spreading a response that no
        // longer carries the key would leave the old one in place.
        setRequests(prev => prev.map(r => (r._id === id ? { ...r, ...res.data, workOrder: null } : r)));
        setActiveRequest(prev => (prev?._id === id ? { ...prev, ...res.data, workOrder: null } : prev));
        await refreshAdminAccountDetail();
      }
      showToast(type === 'work order' ? 'Work order deleted'
        : type === 'invoice' ? 'Invoice deleted'
        : type === 'estimate' ? 'Estimate deleted' : 'Deleted');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleSubmitVendorApp = async () => {
    const f = vendorAppForm;
    if (!f.address || !f.yearsExperience) { alert(t('err.appAddress')); return; }
    if (f.expertise.length === 0) { alert(t('err.appExpertise')); return; }
    if (f.skills.length === 0) { alert(t('err.appSkills')); return; }
    if (!f.hasTools || !f.licensed || !f.insured) { alert(t('err.appYesNo')); return; }
    setIsSubmittingApp(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => {
        fd.append(k, Array.isArray(v) ? JSON.stringify(v) : v);
      });
      vendorDocFiles.forEach(file => fd.append('documents', file));
      const res = await axios.post('/api/vendors/profile', fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      setVendorProfile(res.data);
      setVendorDocFiles([]);
      // The application saves even when a document couldn't be uploaded — say
      // so rather than letting them think the file went through.
      if (res.data.uploadWarning) alert(res.data.uploadWarning);
      else showToast(t('toast.appSubmitted'));
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSubmittingApp(false);
    }
  };

  // Logged-in user changes their own password
  const handleChangePassword = async () => {
    if (passwordForm.next.length < 8) { alert('New password must be at least 8 characters.'); return; }
    if (passwordForm.next !== passwordForm.confirm) { alert('New passwords do not match.'); return; }
    try {
      await axios.post('/api/auth/change-password', {
        currentPassword: passwordForm.current,
        newPassword: passwordForm.next
      }, authHeaders());
      showToast('Password updated!');
      setModal(null);
      setPasswordForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleChangeEmail = async () => {
    if (!/^\S+@\S+\.\S+$/.test(emailForm.next.trim())) { alert('Enter a valid email address.'); return; }
    try {
      const res = await axios.post('/api/auth/change-email', {
        currentPassword: emailForm.current,
        newEmail: emailForm.next,
      }, authHeaders());
      // Keep the signed-in user in step, or the app shows the old address
      // until the next sign-out. The session effect writes it to the device.
      if (res.data.user) setCurrentUser(prev => ({ ...prev, ...res.data.user }));
      showToast('Sign-in email updated');
      setModal(null);
      setEmailForm({ current: '', next: '' });
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Estimate Approval (Homeowner) ─────────────────────────

  const handleApproveEstimate = async (approved) => {
    try {
      await axios.post(`/api/requests/${activeRequest._id}/approve-estimate`, { approved }, authHeaders());
      showToast(approved ? 'Estimate approved!' : 'Estimate declined.');
      await loadHomeownerRequests();
      goTo('screen-homeowner-dashboard');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Completion & Invoice (Vendor) ─────────────────────────

  const invoiceTotal = invoiceForm.lineItems.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0);

  const handleSubmitCompletion = async () => {
    if (completionFiles.length === 0) { alert('Please add at least one photo.'); return; }
    try {
      const fd = new FormData();
      fd.append('note', completionNote);
      completionFiles.forEach(f => fd.append('photos', f));
      await axios.post(`/api/requests/${activeRequest._id}/completion`, fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      showToast('Completion submitted!');
      setModal(null);
      setCompletionFiles([]);
      setCompletionNote('');
      await loadVendorJobs();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmitInvoice = async () => {
    try {
      await axios.post(`/api/requests/${activeRequest._id}/invoice`, {
        amount: invoiceTotal,
        lineItems: invoiceForm.lineItems.filter(r => r.description),
        notes: invoiceForm.notes
      }, authHeaders());
      showToast('Invoice submitted!');
      setModal(null);
      setInvoiceForm({ lineItems: [{ description: '', qty: 1, unitCost: '', total: '' }], notes: '' });
      // Two people raise invoices now — the tech closing out his own job, and
      // Julian billing a management company — and they are looking at two
      // different boards. Refresh whichever one the invoice was raised from.
      if (currentUser?.role === 'admin') {
        await loadAdminData();
        await refreshAdminAccountDetail();
      } else {
        await loadVendorJobs();
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Vendor Ledger ─────────────────────────────────────────

  // Opens the new/edit invoice modal. Pass an entry to edit it, nothing to
  // start a fresh row.
  const openLedgerForm = (entry) => {
    setLedgerForm(entry ? {
      _id: entry._id,
      vendorId: entry.vendorId?._id || entry.vendorId || '',
      requestId: entry.requestId?._id || entry.requestId || '',
      invoiceNumber: entry.invoiceNumber || '',
      serviceAddress: entry.serviceAddress || '',
      jobDetails: entry.jobDetails || '',
      invoiceAmount: entry.invoiceAmount ?? '',
      notes: entry.notes || '',
      status: entry.status || 'open'
    } : {
      _id: '', vendorId: '', requestId: '', invoiceNumber: '',
      serviceAddress: '', jobDetails: '', invoiceAmount: '', notes: '', status: 'open'
    });
    setLedgerDocFiles([]);
    setModal('ledgerEntry');
  };

  // Creates a new ledger row, or saves edits to an existing one.
  const handleSaveLedgerEntry = async () => {
    const f = ledgerForm;
    if (!f.invoiceAmount || parseFloat(f.invoiceAmount) <= 0) { alert('Enter the invoice amount.'); return; }
    if (currentUser?.role === 'admin' && !f._id && !f.vendorId) { alert('Pick which vendor this invoice is from.'); return; }
    setIsSavingLedger(true);
    try {
      if (f._id) {
        await axios.patch(`/api/ledger/${f._id}`, {
          invoiceNumber: f.invoiceNumber, serviceAddress: f.serviceAddress,
          jobDetails: f.jobDetails, invoiceAmount: f.invoiceAmount,
          notes: f.notes, status: f.status
        }, authHeaders());
        showToast('Ledger entry updated');
      } else {
        const fd = new FormData();
        ['vendorId', 'requestId', 'invoiceNumber', 'serviceAddress', 'jobDetails', 'invoiceAmount', 'notes']
          .forEach(k => { if (f[k]) fd.append(k, f[k]); });
        // Lets the server skip the translation call when the writer is already
        // working in English — see server/translate.js.
        fd.append('writerLang', currentLanguage());
        ledgerDocFiles.forEach(file => fd.append('invoiceDocs', file));
        await axios.post('/api/ledger', fd, {
          headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
        });
        showToast(currentUser?.role === 'vendor' ? '🧾 Invoice submitted for payment' : 'Ledger entry added');
      }
      setModal(null);
      setLedgerDocFiles([]);
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSavingLedger(false);
    }
  };

  const handleDeleteLedgerEntry = async (entry) => {
    if (!window.confirm(`Delete invoice ${entry.invoiceNumber}? This can't be undone.`)) return;
    try {
      await axios.delete(`/api/ledger/${entry._id}`, authHeaders());
      showToast('Ledger entry deleted');
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const openRecordPayment = (entry) => {
    setActiveEntry(entry);
    const balance = (entry.invoiceAmount || 0) - (entry.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    setPaymentForm({
      amount: balance > 0 ? balance.toFixed(2) : '',
      date: new Date().toISOString().slice(0, 10),
      mode: 'Check', reference: '', note: '', overrideReason: ''
    });
    setPaymentBlock(null);
    setModal('payment');
  };

  // Records a payment. If the tech's insurance/licenses have lapsed the server
  // refuses with 409 — we surface the blockers and let the admin either fix the
  // paperwork or deliberately override with a written reason.
  const handleRecordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) { alert('Enter a payment amount.'); return; }
    try {
      await axios.post(`/api/ledger/${activeEntry._id}/payments`, paymentForm, authHeaders());
      showToast('💵 Payment recorded');
      setModal(null);
      setPaymentBlock(null);
      await refreshData();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.requiresOverride) {
        setPaymentBlock(err.response.data);
        return; // keep the modal open so they can see why and decide
      }
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeletePayment = async (entry, payment) => {
    if (!window.confirm(`Remove this $${Number(payment.amount).toFixed(2)} payment from ${entry.invoiceNumber}?`)) return;
    try {
      await axios.delete(`/api/ledger/${entry._id}/payments/${payment._id}`, authHeaders());
      showToast('Payment removed');
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const openProgress = (entry) => {
    setActiveEntry(entry);
    setProgressForm({ note: '', percentComplete: '' });
    setProgressFiles([]);
    setModal('progress');
  };

  // Posts a live progress report (photos + note + % complete) onto an invoice.
  const handleAddProgress = async () => {
    if (!progressForm.note && progressFiles.length === 0) { alert('Add a note or at least one photo.'); return; }
    setIsSavingLedger(true);
    try {
      const fd = new FormData();
      fd.append('note', progressForm.note);
      if (progressForm.percentComplete !== '') fd.append('percentComplete', progressForm.percentComplete);
      fd.append('authorName', currentUser?.name || '');
      progressFiles.forEach(file => fd.append('photos', file));
      await axios.post(`/api/ledger/${activeEntry._id}/progress`, fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      showToast('📸 Progress update posted');
      setModal(null);
      setProgressFiles([]);
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSavingLedger(false);
    }
  };

  // Downloads the whole ledger as a CSV laid out like the original spreadsheet.
  const handleExportLedger = async () => {
    try {
      const res = await axios.get('/api/ledger/export/csv', { ...authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vendor-ledger.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error exporting: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Compliance documents ──────────────────────────────────

  // `item` is a row from the compliance panel; vendorId is only passed when an
  // admin is uploading on a tech's behalf.
  const openCredential = (item, vendorId = '') => {
    setCredentialForm({
      vendorId,
      type: item?.type || 'insurance',
      label: item?.type === 'other' ? (item.label || '') : '',
      issuer: item?.issuer || '',
      number: item?.number || '',
      issuedAt: '',
      expiresAt: ''
    });
    setCredentialFile(null);
    setModal('credential');
  };

  const handleSaveCredential = async () => {
    const f = credentialForm;
    if (!f.expiresAt) { alert('Enter the expiration date — that\'s what the renewal reminders are based on.'); return; }
    setIsSavingLedger(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (credentialFile) fd.append('document', credentialFile);
      const res = await axios.post('/api/vendors/credentials', fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      if (currentUser?.role === 'vendor') setVendorProfile(res.data);
      showToast('📄 Document saved');
      setModal(null);
      setCredentialFile(null);
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSavingLedger(false);
    }
  };

  const handleDeleteCredential = async (item, vendorId = '') => {
    if (!window.confirm(`Remove ${item.label}?`)) return;
    try {
      const qs = vendorId ? `?vendorId=${vendorId}` : '';
      const res = await axios.delete(`/api/vendors/credentials/${item._id}${qs}`, authHeaders());
      if (currentUser?.role === 'vendor') setVendorProfile(res.data);
      showToast('Document removed');
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // The homeowner opened a work order, so everything on it has now been seen.
  //
  // The local copy is stamped straight away rather than waiting for a reload —
  // the dot has to clear as the screen opens, not a poll later. The server call
  // is best-effort: failing to record that someone looked at their own job is
  // not worth an error message.
  const markRequestSeen = async (requestId) => {
    if (!requestId || !['homeowner', 'manager'].includes(currentUser?.role)) return;
    const at = new Date().toISOString();
    setRequests(prev => prev.map(r => (r._id === requestId ? { ...r, homeownerSeenAt: at } : r)));
    try {
      await axios.post(`/api/requests/${requestId}/seen`, {}, authHeaders());
    } catch { /* looked at is not a transaction */ }
  };

  // ── Messages ──────────────────────────────────────────────

  // Opens the conversation for a job. Reading it clears that thread's badge.
  const openThread = async (request) => {
    setThreadFor(request);
    setThread([]);
    setMessageText('');
    setMessageFiles([]);
    setModal('messages');
    setIsLoadingThread(true);
    try {
      const res = await axios.get(`/api/messages/${request._id}`, authHeaders());
      setThread(res.data);
      setUnreadCounts(prev => ({ ...prev, [request._id]: 0 }));
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsLoadingThread(false);
    }
  };

  // Re-pull an open thread in the background. No spinner and no scroll reset —
  // this runs on a timer, so it must be invisible unless something new arrived.
  const refreshOpenThread = async (requestId) => {
    try {
      const res = await axios.get(`/api/messages/${requestId}`, authHeaders());
      setThread(prev => (prev.length === res.data.length ? prev : res.data));
      setUnreadCounts(prev => ({ ...prev, [requestId]: 0 }));
    } catch { /* a timer tick is not worth interrupting anyone over */ }
  };

  // The far side of a job thread is someone holding a phone, so the thread has
  // to move on its own — refreshing the page to see a reply is not a
  // conversation. Polls only while the thread is actually open.
  useEffect(() => {
    if (modal !== 'messages' || !threadFor?._id || !authToken) return;
    const id = setInterval(() => refreshOpenThread(threadFor._id), 10000);
    return () => clearInterval(id);
  }, [modal, threadFor, authToken]); // eslint-disable-line

  // A customer's work orders move without them doing anything — an estimate
  // lands, a visit gets booked — so the list refreshes itself while they have
  // it open. Slower than the unread poll: this is "did anything happen today?",
  // not a conversation.
  useEffect(() => {
    if (!authToken || currentScreen !== 'screen-homeowner-dashboard') return;
    const id = setInterval(loadHomeownerRequests, 60000);
    return () => clearInterval(id);
  }, [authToken, currentScreen]); // eslint-disable-line

  // Unread badges keep counting while a dashboard is open, so a new message
  // announces itself without the page being reloaded.
  useEffect(() => {
    if (!authToken || !currentScreen.endsWith('-dashboard')) return;
    const id = setInterval(loadUnread, 20000);
    return () => clearInterval(id);
  }, [authToken, currentScreen]); // eslint-disable-line

  // Say so when the count goes up. Deliberately not the browser's Notification
  // API: on iOS that only works for an installed PWA, so it would do nothing on
  // the phone Julian actually uses and look like another broken feature.
  const prevUnread = useRef(null);
  useEffect(() => {
    const total = Object.values(unreadCounts).reduce((sum, n) => sum + n, 0);
    // First reading is the baseline, never an alert.
    if (prevUnread.current !== null && total > prevUnread.current) {
      showToast(total - prevUnread.current === 1 ? 'New message' : `${total - prevUnread.current} new messages`);
    }
    prevUnread.current = total;
  }, [unreadCounts]); // eslint-disable-line

  // ── Installing, and working on a bad signal ───────────────
  // The worker is registered on every visit, not only when notifications are
  // switched on: it is also what makes the app open and job data readable with
  // no signal, which matters in the crawlspaces techs actually work in.
  useEffect(() => { registerServiceWorker(); }, []);

  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const online = () => setIsOffline(false);
    const offline = () => setIsOffline(true);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, []);

  // Chrome decides when installing is possible and tells us via an event, so
  // the button has to appear and disappear on its own.
  const [canInstall, setCanInstall] = useState(canPromptInstall());
  useEffect(() => onInstallAvailabilityChange(setCanInstall), []);

  const handleInstall = async () => {
    const accepted = await promptInstall();
    if (accepted) showToast('Installed — open it from your home screen');
  };

  // ── Push notifications ────────────────────────────────────
  // The ones that arrive when the site is shut. See client/src/lib/push.js for
  // why iOS makes this conditional on being installed to the Home Screen.
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!authToken) { setPushOn(false); return; }
    isPushOnThisDevice().then(setPushOn);
  }, [authToken]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush(authHeaders());
        setPushOn(false);
        showToast('Notifications turned off on this device');
      } else {
        await enablePush(authHeaders());
        setPushOn(true);
        showToast('Notifications are on for this device');
      }
    } catch (err) {
      alert(err.message || 'Could not change notifications');
    } finally {
      setPushBusy(false);
    }
  };

  // Proves the whole round trip, which you otherwise cannot check without
  // waiting for a real customer to text you.
  const sendTestPush = async () => {
    try {
      const res = await axios.post('/api/push/test', {}, authHeaders());
      showToast(res.data.sent > 0
        ? `Test sent to ${res.data.sent} device${res.data.sent > 1 ? 's' : ''}`
        : 'No devices are signed up for notifications yet');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Every inbound text Twilio handed us and what became of it. Answers "did my
  // reply arrive?" without a Render login.
  const refreshSmsStatus = async () => {
    setIsLoadingSms(true);
    try {
      const res = await axios.get('/api/messages/sms-status', authHeaders());
      setSmsStatus({ ...res.data, loaded: true });
    } catch (err) {
      setSmsStatus({ enabled: false, events: [], loaded: true, error: err.response?.data?.message || err.message });
    } finally {
      setIsLoadingSms(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() && messageFiles.length === 0) return;
    setIsSendingMessage(true);
    try {
      const fd = new FormData();
      fd.append('body', messageText);
      messageFiles.forEach(f => fd.append('attachments', f));
      const res = await axios.post(`/api/messages/${threadFor._id}`, fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      setThread(prev => [...prev, res.data]);
      setMessageText('');
      setMessageFiles([]);
      // The message saved but a file was refused — say which, or they'll think
      // the photo went out. Cloudinary's own wording is the useful part.
      if (res.data.uploadWarning) {
        alert(`Your message was sent, but the file could not be attached: ${res.data.uploadWarning}\n\nTry a smaller file, or a photo instead of a video.`);
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSendingMessage(false);
    }
  };

  // ── Projects ──────────────────────────────────────────────

  const refreshProjects = async () => {
    try {
      const res = await axios.get('/api/projects', authHeaders());
      setProjects(res.data);
      // Keep the open project in sync after an edit.
      if (activeProject) {
        const fresh = res.data.find(p => p._id === activeProject._id);
        if (fresh) setActiveProject(fresh);
      }
    } catch { /* silently fail */ }
  };

  const openProjectForm = (project) => {
    setProjectForm(project ? {
      _id: project._id, title: project.title || '',
      homeownerId: project.homeownerId?._id || project.homeownerId || '',
      clientName: project.clientName || '', address: project.address || '',
      status: project.status || 'active', notes: project.notes || ''
    } : { _id: '', title: '', homeownerId: '', clientName: '', address: '', status: 'active', notes: '' });
    setModal('project');
  };

  const handleSaveProject = async () => {
    if (!projectForm.title) { alert('Give the project a name.'); return; }
    setIsSavingProject(true);
    try {
      const body = { ...projectForm };
      delete body._id;
      if (projectForm._id) await axios.patch(`/api/projects/${projectForm._id}`, body, authHeaders());
      else await axios.post('/api/projects', body, authHeaders());
      showToast(projectForm._id ? 'Project updated' : 'Project created');
      setModal(null);
      await refreshProjects();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  const handleDeleteProject = async (project) => {
    if (!window.confirm(`Delete "${project.title}" and everything on it?`)) return;
    try {
      await axios.delete(`/api/projects/${project._id}`, authHeaders());
      setActiveProject(null);
      showToast('Project deleted');
      await refreshProjects();
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  const openProjectLine = (project, line) => {
    setActiveProject(project);
    setLineForm(line ? {
      _id: line._id, trade: line.trade || '', invoiceNumber: line.invoiceNumber || '',
      invoiceUrl: line.invoiceUrl || '', amount: line.amount ?? '',
      vendorId: line.vendorId || '', techCost: line.techCost ?? ''
    } : { _id: '', trade: '', invoiceNumber: '', invoiceUrl: '', amount: '', vendorId: '', techCost: '' });
    setModal('projectLine');
  };

  // ── Invoicing a client ────────────────────────────────────────────────
  // An invoice here is a dated, numbered set of lines on that client's project
  // statement — the same statement the homeowner reads in their portal and the
  // one Customer Snapshots add up. One sheet serves both ways in: a blank
  // invoice from their page, and an estimate converted into one.
  const [clientInvoiceForm, setClientInvoiceForm] = useState(null);
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);

  const todayInput = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // ── More, on a document ────────────────────────────────────────────────
  // Duplicate is the same "new" flow the buttons already open, arriving with
  // last time's lines in it. A repeat customer's third turn is the second one
  // with two lines changed, and retyping it is how a price drifts.
  const duplicateEstimate = (request) => {
    const client = {
      _id: request.homeownerId?._id || request.homeownerId,
      name: request.homeownerId?.name || request.fullName,
      phone: request.phone, email: request.email,
      address: request.serviceAddress, city: request.city,
    };
    setPrintDoc(null);
    openEstimateForClient(client);
    setEstimateForm({
      breakdown: (request.estimate?.breakdown || []).map(r => ({
        label: r.label || '',
        description: r.description || '',
        photos: r.photos || [],
        cost: r.cost != null ? String(r.cost) : '',
        qty: r.qty != null ? String(r.qty) : '',
        rate: r.rate != null ? String(r.rate) : '',
      })),
      notes: request.estimate?.notes || '',
    });
  };

  const duplicateInvoice = (invoice) => {
    if (!invoice?.client) return;
    setPrintDoc(null);
    openClientInvoice(invoice.client);
    setClientInvoiceForm(p => ({
      ...p,
      projectId: invoice.project?._id || '',
      title: invoice.project?.title || '',
      lines: (invoice.lines || [])
        .filter(l => !['discount', 'tax'].includes((l.trade || '').trim().toLowerCase()))
        .map(l => ({
          label: l.trade || '',
          description: l.notes || '',
          photos: [],
          cost: l.amount != null ? String(l.amount) : '',
          qty: l.qty != null ? String(l.qty) : '',
          rate: l.rate != null ? String(l.rate) : '',
        })),
    }));
  };

  const askDeleteEstimate = (request) => {
    setPrintDoc(null);
    setConfirmDelete({
      type: 'estimate', id: request._id,
      label: [request.items?.[0]?.title || request.serviceType, fmtMoney(request.estimate?.amount)].filter(Boolean).join(' · '),
    });
  };

  const askDeleteInvoice = (invoice) => {
    setPrintDoc(null);
    setConfirmDelete({
      type: 'invoice', id: invoice.project?._id, number: invoice.number,
      label: [invoice.number ? `#${invoice.number}` : 'This invoice', fmtMoney(invoice.total)].filter(Boolean).join(' · '),
    });
  };

  // One invoice, as the sheet of paper the customer gets.
  const openInvoiceDoc = (invoice) => setPrintDoc({ invoice, kind: 'invoice' });

  const openClientInvoice = (client) => {
    setClientInvoiceForm({
      client,
      requestId: '',
      // Blank means a new project. Most invoices belong to one already —
      // billing a basement finish in five trades is five invoices on one
      // statement, not five projects.
      projectId: '',
      title: '',
      invoiceNumber: '',
      invoiceDate: todayInput(),
      discount: '',
      tax: '',
      lines: [{ label: '', description: '', photos: [], cost: '' }],
    });
    setModal('clientInvoice');
  };

  // The Invoice button on an estimate: the price that was agreed, carried over
  // line for line so nothing is retyped and nothing drifts from what the
  // customer said yes to. Still editable — the job on site is rarely the job
  // on paper to the penny.
  const openInvoiceFromEstimate = (client, request) => {
    const rows = (request.estimate?.breakdown || []).filter(r => r.label || r.cost);
    setClientInvoiceForm({
      client,
      requestId: request._id,
      projectId: '',
      title: request.items?.[0]?.title || request.serviceType || 'Work',
      invoiceNumber: '',
      invoiceDate: todayInput(),
      discount: '',
      tax: '',
      // Line for line, scope and photos included: the customer agreed to a
      // description as much as to a number, and the bill should say the same
      // thing the estimate did.
      lines: rows.length
        ? rows.map(r => ({
            label: r.label || request.serviceType || 'Work',
            description: r.description || '',
            photos: r.photos || [],
            cost: String(r.cost ?? ''),
          }))
        : [{
            label: request.items?.[0]?.title || request.serviceType || 'Work',
            description: request.items?.[0]?.description || '',
            photos: [],
            cost: String(request.estimate?.amount ?? ''),
          }],
    });
    setModal('clientInvoice');
  };

  const handleSaveClientInvoice = async () => {
    const f = clientInvoiceForm;
    if (!f) return;
    const lines = f.lines
      .map(l => ({
        trade: (l.label || '').trim(),
        amount: parseFloat(l.cost) || 0,
        // The scope goes onto the statement line's notes, which is where the
        // homeowner's own statement already reads it from.
        notes: (l.description || '').trim(),
      }))
      .filter(l => l.trade || l.amount);
    if (!lines.length) { alert('An invoice needs at least one line.'); return; }
    if (!f.projectId && !f.title.trim()) { alert('Name the project this invoice belongs to.'); return; }
    setIsSavingInvoice(true);
    try {
      // A date input gives a bare day, which the server stores as UTC midnight
      // — and Utah is six hours behind, so the statement would print the day
      // before. Sent as noon that day instead, it reads the same whether the
      // page reads it in local time or in UTC (which the statement does).
      const [y, m, d] = (f.invoiceDate || '').split('-').map(Number);
      const invoiceDate = y ? new Date(y, m - 1, d, 12).toISOString() : undefined;
      // A discount and the tax are lines of their own on the statement, under
      // the same invoice number as the work. Kept as lines rather than as
      // fields on the invoice because everything downstream — the statement,
      // what they owe, the Customer Snapshots — already adds lines up, and a
      // total that only some screens know how to work out is a total that
      // disagrees with itself.
      const discount = parseFloat(f.discount) || 0;
      const tax = parseFloat(f.tax) || 0;
      if (discount > 0) lines.push({ trade: 'Discount', amount: -discount, notes: '' });
      if (tax > 0) lines.push({ trade: 'Tax', amount: tax, notes: '' });

      const res = await axios.post('/api/projects/client-invoice', {
        homeownerId: f.client._id,
        requestId: f.requestId || null,
        projectId: f.projectId || null,
        title: f.title.trim(),
        invoiceNumber: f.invoiceNumber.trim(),
        invoiceDate,
        lines,
      }, authHeaders());
      const number = res.data?.lines?.[res.data.lines.length - 1]?.invoiceNumber;
      showToast(number ? `Invoice ${number} raised` : 'Invoice raised');
      setModal(null);
      setClientInvoiceForm(null);
      await loadAdminData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingInvoice(false); }
  };

  const handleSaveProjectLine = async () => {
    if (!lineForm.trade) { alert('Name the trade or work.'); return; }
    setIsSavingProject(true);
    try {
      const body = { ...lineForm };
      delete body._id;
      if (lineForm._id) await axios.patch(`/api/projects/${activeProject._id}/lines/${lineForm._id}`, body, authHeaders());
      else await axios.post(`/api/projects/${activeProject._id}/lines`, body, authHeaders());
      showToast('Statement updated');
      setModal(null);
      await refreshProjects();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  const handleDeleteProjectLine = async (project, line) => {
    if (!window.confirm(`Remove "${line.trade}" from the statement?`)) return;
    try {
      await axios.delete(`/api/projects/${project._id}/lines/${line._id}`, authHeaders());
      showToast('Line removed');
      await refreshProjects();
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  const openProjectPayment = (project, line) => {
    setActiveProject(project);
    setClientPayForm({
      lineId: line?._id || '',
      amount: line?.owed > 0 ? Number(line.owed).toFixed(2) : '',
      date: new Date().toISOString().slice(0, 10),
      method: 'Check', reference: ''
    });
    setModal('clientPayment');
  };

  const handleSaveClientPayment = async () => {
    if (!clientPayForm.amount || parseFloat(clientPayForm.amount) <= 0) { alert('Enter an amount.'); return; }
    setIsSavingProject(true);
    try {
      await axios.post(`/api/projects/${activeProject._id}/payments`, clientPayForm, authHeaders());
      showToast('Payment recorded');
      setModal(null);
      await refreshProjects();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  const handleDeleteClientPayment = async (project, payment) => {
    if (!window.confirm('Remove this payment?')) return;
    try {
      await axios.delete(`/api/projects/${project._id}/payments/${payment._id}`, authHeaders());
      await refreshProjects();
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  const openProjectExpense = (project) => {
    setActiveProject(project);
    setExpenseForm({ lineId: '', description: '', supplier: '', amount: '', paymentDetails: '', chargeToVendorId: '' });
    setReceiptFile(null);
    setModal('projectExpense');
  };

  const handleSaveExpense = async () => {
    if (!expenseForm.amount || parseFloat(expenseForm.amount) <= 0) { alert('Enter the amount spent.'); return; }
    setIsSavingProject(true);
    try {
      const fd = new FormData();
      Object.entries(expenseForm).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (receiptFile) fd.append('receipt', receiptFile);
      await axios.post(`/api/projects/${activeProject._id}/expenses`, fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      showToast('Expense logged');
      setModal(null);
      setReceiptFile(null);
      await refreshProjects();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  const handleDeleteProjectExpense = async (project, expense) => {
    if (!window.confirm('Remove this expense?')) return;
    try {
      await axios.delete(`/api/projects/${project._id}/expenses/${expense._id}`, authHeaders());
      await refreshProjects();
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  // ── Technician estimates ──────────────────────────────────

  const estimateItemsTotal = estimateForm2.items.reduce(
    (t, i) => t + (parseFloat(i.qty) || 0) * (parseFloat(i.unitCost) || 0), 0
  );

  const openEstimateForm = () => {
    setEstimateForm2({ trade: '', serviceAddress: '', notes: '', items: [{ name: '', description: '', kind: 'labor', qty: 1, unitCost: '', photos: [] }] });
    setModal('techEstimate');
  };

  const handleSubmitTechEstimate = async () => {
    const items = estimateForm2.items.filter(i => i.description || i.name);
    if (items.length === 0) { alert('Add at least one line.'); return; }
    setIsSavingProject(true);
    try {
      await axios.post('/api/estimates', { ...estimateForm2, items, writerLang: currentLanguage() }, authHeaders());
      showToast('Estimate sent to Quick Property Services');
      setModal(null);
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  const handleApproveEstimateToProject = async (estimate, projectId, invoiceNumber, invoiceUrl) => {
    try {
      await axios.post(`/api/estimates/${estimate._id}/approve`,
        { projectId, invoiceNumber, invoiceUrl }, authHeaders());
      showToast('Approved — added to the project statement');
      await refreshData();
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  // ══ Commercial Accounts ═══════════════════════════════════════════════
  //
  // The fourth portal. All of it hangs off one membership resolved at sign-in,
  // and every read is already scoped to that account by the server, so nothing
  // here filters for access — only for display.

  const [account, setAccount] = useState(null);
  const [accountMembers, setAccountMembers] = useState([]);
  const [accountView, setAccountView] = useState('jobs');
  const [properties, setProperties] = useState([]);
  const [propertyTotal, setPropertyTotal] = useState(0);
  const [propertySearch, setPropertySearch] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyHistory, setPropertyHistory] = useState([]);
  const [propertyForm, setPropertyForm] = useState(null);
  const [unitForm, setUnitForm] = useState(null);
  const [memberForm, setMemberForm] = useState(null);
  const [woForm, setWoForm] = useState(null);
  const [importState, setImportState] = useState(null);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState('');

  // Julian's own view of the commercial accounts. Separate from the manager's
  // state above because it is a different question: that one is "my company",
  // this one is "all of them".
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [adminAccountDetail, setAdminAccountDetail] = useState(null);

  const loadAdminAccounts = async () => {
    try {
      const res = await axios.get('/api/accounts', authHeaders());
      setAdminAccounts(res.data);
    } catch { /* silently fail */ }
  };

  // Re-read whichever account is open, if one is. Called after anything that
  // changes a job at one of its properties.
  const refreshAdminAccountDetail = async () => {
    const id = adminAccountDetail?.account?._id || adminAccountDetail?.account?.id;
    if (id) await openAdminAccount(id);
  };

  const openAdminAccount = async (id) => {
    if (!id) { setAdminAccountDetail(null); return; }
    try {
      const res = await axios.get(`/api/accounts/${id}`, authHeaders());
      setAdminAccountDetail(res.data);
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // Julian setting a commercial account up himself.
  //
  // Self-signup at /commercial is for the company that finds us. This is for the
  // one he is already working with, who will not go and fill in a web form
  // because he asked them to — he takes their details down the phone and gets
  // them a login before the call ends.
  const [accountForm, setAccountForm] = useState(null);
  const [accountMemberForm, setAccountMemberForm] = useState(null);
  // The set-password link from the last invite. Kept on screen because reading
  // it down the phone is the most reliable way to get somebody into a portal —
  // more reliable than an email that lands in a spam folder nobody checks.
  const [lastInviteLink, setLastInviteLink] = useState('');

  const openAccountForm = () => {
    setAccountForm({
      name: '', type: 'property-management', phone: '', email: '',
      billingEmail: '', billingAddress: '', defaultNteAmount: '',
      ownerName: '', ownerEmail: '', ownerPhone: '', sendInvite: true,
    });
    setLastInviteLink('');
    setModal('adminAccount');
  };

  const handleCreateAccount = async () => {
    const f = accountForm || {};
    if (!f.name?.trim()) { alert('A company name is required.'); return; }
    try {
      const res = await axios.post('/api/accounts', f, authHeaders());
      await loadAdminAccounts();
      if (res.data?.warning) {
        alert(res.data.warning);
      } else {
        showToast(res.data?.owner ? 'Company created and invite sent' : 'Company created');
      }
      // Keep the modal open when there is a link worth reading out; otherwise
      // get out of the way.
      if (res.data?.owner?.link) {
        setLastInviteLink(res.data.owner.link);
        setAccountForm(null);
      } else {
        setAccountForm(null);
        setModal(null);
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const openAccountMemberForm = (account, member) => {
    setAccountMemberForm({
      accountId: account.id || account._id,
      accountName: account.name,
      userId: member?.userId || null,
      name: member?.name || '',
      email: member?.email || '',
      phone: member?.phone || '',
      level: member?.level || 'coordinator',
      sendInvite: true,
    });
    setLastInviteLink('');
    setModal('adminAccountMember');
  };

  const handleSaveAccountMember = async () => {
    const f = accountMemberForm || {};
    try {
      if (f.userId) {
        await axios.patch(`/api/accounts/${f.accountId}/members/${f.userId}`, { level: f.level }, authHeaders());
        showToast('Access level updated');
        setModal(null);
      } else {
        if (!f.name?.trim() || !f.email?.trim()) { alert('A name and email are both needed.'); return; }
        const res = await axios.post(`/api/accounts/${f.accountId}/members`, f, authHeaders());
        showToast(f.sendInvite === false ? 'Added — send them the link' : 'Added and invite sent');
        setLastInviteLink(res.data?.link || '');
        if (!res.data?.link) setModal(null);
      }
      setAccountMemberForm(null);
      await openAdminAccount(f.accountId);
      await loadAdminAccounts();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Julian adding a property to a company himself.
  //
  // The company can do this from their own portal, but on the day an account is
  // brought on board nobody there has signed in yet — he takes the buildings
  // down the phone with the rest of their details. The modal asks for the
  // address and the things a tech is stuck without; everything else on the
  // property record they can fill in later from their side.
  const [adminPropertyForm, setAdminPropertyForm] = useState(null);

  const openAdminPropertyForm = (account) => {
    setAdminPropertyForm({
      accountId: account.id || account._id,
      accountName: account.name,
      addressLine1: '', addressLine2: '', city: '', state: 'UT', zip: '',
      propertyType: 'single-family', label: '', externalId: '', nteAmount: '',
      access: { gateCode: '', lockboxCode: '' },
    });
    setModal('adminProperty');
  };

  const handleCreateAdminProperty = async () => {
    const f = adminPropertyForm || {};
    if (!f.addressLine1?.trim()) { alert('A street address is required.'); return; }
    try {
      const { accountId, accountName, ...body } = f;
      await axios.post(`/api/accounts/${accountId}/properties`, body, authHeaders());
      setAdminPropertyForm(null);
      setModal(null);
      showToast('Property added');
      // The detail screen counts and lists them; the board behind it shows the
      // property count on the card, so both are stale until they're re-read.
      await openAdminAccount(accountId);
      await loadAdminAccounts();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── A property's own page ────────────────────────────────────────────
  //
  // Tapping a property opens what has happened at that building: the access
  // codes, the units, and every job with its price. It is also where a job
  // STARTS, because most commercial work never comes in through the portal —
  // Julian walks a turn with the manager and quotes it on the spot.
  //
  // Only the id is kept. The property and its jobs are read back out of
  // adminAccountDetail, so a refresh after saving an estimate updates this
  // screen without a second copy of the same record drifting behind it.
  const [adminPropertyId, setAdminPropertyId] = useState(null);

  // What a job needs before it can be priced. Deliberately short: the address,
  // the unit and the limit all come off the property, so the only thing left is
  // what the work IS — and Julian is standing in front of it.
  const [propertyJobForm, setPropertyJobForm] = useState(null);

  const openPropertyJobForm = (account, property, next) => {
    setPropertyJobForm({
      accountId: account.id || account._id,
      propertyId: property._id,
      propertyLabel: property.label || property.addressLine1,
      units: (property.units || []).filter((u) => u.isActive !== false),
      unitId: '',
      serviceType: 'Handyman – General Repair',
      description: '',
      priority: 'routine',
      // Which sheet opens once the job exists — the price, or the bill.
      next,
    });
    setModal('adminPropertyJob');
  };

  // Creates the job, then hands straight over to the estimate or invoice sheet.
  // Two saves in a row is the honest shape of it — a price belongs to a job —
  // but it should feel like one action, so the sheet opens itself.
  const handleCreatePropertyJob = async () => {
    const f = propertyJobForm || {};
    if (!f.description?.trim()) { alert('Say what the work is.'); return; }
    try {
      const res = await axios.post(
        `/api/accounts/${f.accountId}/properties/${f.propertyId}/jobs`,
        { unitId: f.unitId || undefined, serviceType: f.serviceType, description: f.description, priority: f.priority },
        authHeaders()
      );
      setPropertyJobForm(null);
      await loadAdminData();
      await openAdminAccount(f.accountId);
      if (f.next === 'invoice') openAdminInvoice(res.data);
      else if (f.next === 'estimate') openEstimate(res.data);
      // A plain service request: the job is the whole point of it. It lands on
      // the board like any other and gets priced when it gets priced.
      else { setModal(null); showToast('Service request logged'); }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // The invoice sheet, opened by Julian rather than by a tech closing out.
  //
  // `seed` decides what is already typed in it:
  //   · an invoice that exists — read and correct it,
  //   · 'estimate' — the estimate's own rows, which is what "convert" means:
  //     the price they agreed becomes the bill, line for line, still editable
  //     because the job that got done is never exactly the job that got quoted,
  //   · nothing — an empty bill, for the work that was never estimated.
  const openAdminInvoice = (request, seed) => {
    setActiveRequest(request);
    const fromInvoice = request.invoice?.lineItems?.length
      ? request.invoice.lineItems.map((r) => ({
          description: r.description || '',
          qty: r.qty != null ? String(r.qty) : '1',
          unitCost: r.unitCost != null ? String(r.unitCost) : '',
          total: r.total != null ? String(r.total) : '',
        }))
      : null;
    const fromEstimate = request.estimate?.breakdown?.length
      ? request.estimate.breakdown.map((r) => {
          // A breakdown row may carry its workings — "3 hrs at $85" — or just a
          // price. An invoice line always has a qty and a unit cost, so the
          // workings come across when there are any, and a row without them
          // becomes one of itself at its own price.
          //
          // Never divided out to invent a unit cost: $100 across 3 would bill
          // $99.99, and the number they agreed to is the number they get.
          const cost = Number(r.cost) || 0;
          const rate = Number(r.rate) || 0;
          const qty = Number(r.qty) || 0;
          const useWorkings = rate > 0 && qty > 0;
          return {
            description: r.label || '',
            qty: String(useWorkings ? qty : 1),
            unitCost: String(useWorkings ? rate : cost),
            total: (useWorkings ? qty * rate : cost).toFixed(2),
          };
        })
      : null;
    const rows = (seed === 'estimate' ? fromEstimate : fromInvoice)
      || [{ description: '', qty: 1, unitCost: '', total: '' }];
    setInvoiceForm({
      lineItems: rows,
      notes: (seed === 'estimate' ? request.estimate?.notes : request.invoice?.notes) || '',
    });
    setModal('invoice');
  };

  // Photos, video, receipts and PDFs kept against a property, from the admin
  // board. Same component and same shape as the manager's own list; a different
  // route only because that one scopes by account membership, which Julian
  // hasn't got.
  const addAdminPropertyDocuments = async (files, meta) => {
    const d = adminAccountDetail;
    if (!d || !adminPropertyId) return;
    const docs = await uploadDocuments(files, meta);
    if (!docs.length) return;
    const accountId = d.account._id || d.account.id;
    try {
      await axios.post(`/api/accounts/${accountId}/properties/${adminPropertyId}/documents`, { documents: docs }, authHeaders());
      await openAdminAccount(accountId);
      showToast(t('doc.saved'));
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const removeAdminPropertyDocument = async (doc) => {
    const d = adminAccountDetail;
    if (!d || !adminPropertyId || !window.confirm(t('doc.confirmRemove'))) return;
    const accountId = d.account._id || d.account.id;
    try {
      await axios.delete(`/api/accounts/${accountId}/properties/${adminPropertyId}/documents/${doc._id}`, authHeaders());
      await openAdminAccount(accountId);
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // Attaching to a JOB from the property page. addRequestDocuments already does
  // this for the screen that has an activeRequest; here the job is one row of a
  // list, so it takes the request it is given and refreshes what is on screen.
  const addJobDocuments = async (request, files, meta) => {
    const docs = await uploadDocuments(files, meta);
    if (!docs.length) return;
    try {
      await axios.post(`/api/requests/${request._id}/documents`, { documents: docs }, authHeaders());
      await loadAdminData();
      await refreshAdminAccountDetail();
      showToast(t('doc.saved'));
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const handleRemoveAccountMember = async (accountId, member) => {
    if (!window.confirm(`Remove ${member.name} from this account?`)) return;
    try {
      await axios.delete(`/api/accounts/${accountId}/members/${member.userId}`, authHeaders());
      await openAdminAccount(accountId);
      showToast('Removed');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const loadAccount = async () => {
    try {
      const res = await axios.get('/api/accounts/me', authHeaders());
      setAccount(res.data.account);
      setAccountMembers(res.data.members || []);
    } catch { /* silently fail — the portal still lists work orders */ }
  };

  // Searched on the server. Four hundred properties is the opening position,
  // and filtering a whole portfolio in the browser is how this gets slow.
  const loadProperties = async (q) => {
    try {
      const res = await axios.get('/api/properties', {
        ...authHeaders(),
        params: { q: q ?? propertySearch, limit: 200 },
      });
      setProperties(res.data.properties || []);
      setPropertyTotal(res.data.total ?? 0);
    } catch { /* silently fail */ }
  };

  const loadAccountData = async () => {
    try {
      const rRes = await axios.get('/api/requests', authHeaders());
      setRequests(rRes.data);
    } catch { /* silently fail */ }
    await Promise.all([loadAccount(), loadProperties('')]);
  };

  // Opening a work order from the Commercial Accounts list.
  //
  // Deliberately the SAME screen the homeowner portal uses: a work order reads
  // the same whoever raised it — the jobs, the photos, the appointment window,
  // the timeline, the estimate — and maintaining two of them would guarantee
  // they drifted apart.
  const openRequestDetailScreen = (r) => {
    setActiveRequest(r);
    goTo('screen-request-detail');
  };

  const openProperty = async (p) => {
    setSelectedProperty(p);
    setPropertyHistory([]);
    goTo('screen-property-detail');
    try {
      const res = await axios.get(`/api/properties/${p._id}`, authHeaders());
      setSelectedProperty(res.data.property);
      setPropertyHistory(res.data.history || []);
    } catch { /* the summary we already have still renders */ }
  };

  const openPropertyForm = (p) => {
    setFormError('');
    setPropertyForm(p ? { ...p } : { state: 'UT', propertyType: 'single-family', access: {}, building: {}, owner: {} });
    goTo('screen-property-form');
  };
  const closePropertyForm = () => {
    setPropertyForm(null);
    goTo(selectedProperty ? 'screen-property-detail' : 'screen-account-dashboard');
  };

  const saveProperty = async () => {
    const f = propertyForm || {};
    if (!String(f.addressLine1 || '').trim()) { setFormError(t('acct.errAddress')); return; }
    setFormBusy(true); setFormError('');
    try {
      const body = { ...f };
      // An empty box means "use the company default", which is not the same as
      // a limit of zero — so it has to travel as null rather than as "".
      body.nteAmount = f.nteAmount === '' || f.nteAmount == null ? null : Number(f.nteAmount);
      if (f._id) {
        const res = await axios.patch(`/api/properties/${f._id}`, body, authHeaders());
        setSelectedProperty(res.data);
      } else {
        await axios.post('/api/properties', body, authHeaders());
      }
      await loadProperties();
      setPropertyForm(null);
      showToast(t('acct.propertySaved'));
      goTo(f._id ? 'screen-property-detail' : 'screen-account-dashboard');
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  const removeProperty = async (p) => {
    if (!window.confirm(t('acct.confirmRemoveProperty'))) return;
    try {
      await axios.delete(`/api/properties/${p._id}`, authHeaders());
      setSelectedProperty(null);
      await loadProperties();
      showToast(t('acct.propertyRemoved'));
      goTo('screen-account-dashboard');
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const addPropertyDocuments = async (files, meta) => {
    const p = selectedProperty;
    if (!p) return;
    const docs = await uploadDocuments(files, meta);
    if (!docs.length) return;
    try {
      const res = await axios.post(`/api/properties/${p._id}/documents`, { documents: docs }, authHeaders());
      setSelectedProperty(res.data);
      showToast(t('doc.saved'));
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const removePropertyDocument = async (doc) => {
    const p = selectedProperty;
    if (!p || !window.confirm(t('doc.confirmRemove'))) return;
    try {
      await axios.delete(`/api/properties/${p._id}/documents/${doc._id}`, authHeaders());
      const res = await axios.get(`/api/properties/${p._id}`, authHeaders());
      setSelectedProperty(res.data.property);
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // Attaching a file to a job that already exists — from the work order screen,
  // in any of the three portals that can see it.
  const addRequestDocuments = async (files, meta) => {
    const r = activeRequest;
    if (!r) return;
    const docs = await uploadDocuments(files, meta);
    if (!docs.length) return;
    try {
      const res = await axios.post(`/api/requests/${r._id}/documents`, { documents: docs }, authHeaders());
      setActiveRequest(res.data);
      setRequests(prev => prev.map(x => (x._id === res.data._id ? res.data : x)));
      showToast(t('doc.saved'));
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // On the work-order FORM the job does not exist yet, so files are uploaded
  // and held on the form until it posts.
  const addWorkOrderDocuments = async (files, meta) => {
    const docs = await uploadDocuments(files, meta);
    if (!docs.length) return;
    setWoForm(prev => ({ ...prev, documents: [...(prev?.documents || []), ...docs] }));
  };

  const openUnitForm = (p, u) => {
    setFormError('');
    setSelectedProperty(p);
    setUnitForm(u ? { ...u } : { occupancy: 'occupied' });
    goTo('screen-unit-form');
  };
  const closeUnitForm = () => { setUnitForm(null); goTo('screen-property-detail'); };

  const saveUnit = async () => {
    const u = unitForm || {};
    const p = selectedProperty;
    if (!p) return;
    if (!String(u.unitNumber || '').trim()) { setFormError(t('acct.errUnitNumber')); return; }
    setFormBusy(true); setFormError('');
    try {
      const res = u._id
        ? await axios.patch(`/api/properties/${p._id}/units/${u._id}`, u, authHeaders())
        : await axios.post(`/api/properties/${p._id}/units`, u, authHeaders());
      setSelectedProperty(res.data);
      setUnitForm(null);
      await loadProperties();
      showToast(t('acct.unitSaved'));
      goTo('screen-property-detail');
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  const deleteUnit = async () => {
    const u = unitForm; const p = selectedProperty;
    if (!u?._id || !p || !window.confirm(t('acct.confirmRemoveUnit'))) return;
    try {
      await axios.delete(`/api/properties/${p._id}/units/${u._id}`, authHeaders());
      const res = await axios.get(`/api/properties/${p._id}`, authHeaders());
      setSelectedProperty(res.data.property);
      setUnitForm(null);
      goTo('screen-property-detail');
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const openMemberForm = (m) => {
    setFormError('');
    setMemberForm(m ? { ...m } : { level: 'coordinator' });
    goTo('screen-member-form');
  };
  const closeMemberForm = () => { setMemberForm(null); goTo('screen-account-dashboard'); };

  const saveMember = async () => {
    const m = memberForm || {};
    setFormBusy(true); setFormError('');
    try {
      if (m.userId) {
        await axios.patch(`/api/accounts/me/members/${m.userId}`, { level: m.level }, authHeaders());
      } else {
        if (!m.name?.trim() || !m.email?.trim()) { setFormError(t('acct.errNameEmail')); setFormBusy(false); return; }
        await axios.post('/api/accounts/me/members', m, authHeaders());
      }
      await loadAccount();
      setMemberForm(null);
      showToast(m.userId ? t('acct.memberSaved') : t('acct.inviteSent'));
      goTo('screen-account-dashboard');
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(t('acct.confirmRemoveMember'))) return;
    try {
      await axios.delete(`/api/accounts/me/members/${m.userId}`, authHeaders());
      await loadAccount();
      setMemberForm(null);
      goTo('screen-account-dashboard');
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const saveAccountSettings = async (patch) => {
    try {
      const res = await axios.patch('/api/accounts/me', patch, authHeaders());
      setAccount(res.data);
      showToast(t('acct.settingsSaved'));
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // Raising a work order. `property` pre-selects one when it was opened from
  // that property's own screen.
  // Who the company can ask for by name. A name and their trades, nothing else
  // — see GET /api/vendors/for-account for why that list is deliberately thin.
  const [accountTechs, setAccountTechs] = useState([]);
  const loadAccountTechs = async () => {
    try {
      const res = await axios.get('/api/vendors/for-account', authHeaders());
      setAccountTechs(res.data || []);
    } catch { /* the form still works; it just cannot name anybody */ }
  };

  const openAccountWorkOrder = (property) => {
    setFormError('');
    setWoForm({
      property: property || null,
      unitId: '',
      items: [{ title: '', serviceType: '', description: '', photos: [], files: [] }],
      priority: 'routine',
      nteAmount: '',
      access: {},
      documents: [],
      requestedVendorId: '',
    });
    if (!accountTechs.length) loadAccountTechs();
    goTo('screen-account-wo');
  };
  const closeAccountWorkOrder = () => { setWoForm(null); goTo('screen-account-dashboard'); };

  const submitAccountWorkOrder = async () => {
    const w = woForm || {};
    if (!w.property) { setFormError(t('acct.errPickProperty')); return; }
    const items = (w.items || []).filter(i => (i.title || '').trim() || (i.description || '').trim());
    if (!items.length) { setFormError(t('acct.errDescribeJob')); return; }

    setFormBusy(true); setFormError('');
    try {
      // Any photo still held as a File goes up first, so the work order itself
      // posts as plain JSON — the same shape Julian's Add-a-Lead uses.
      const withPhotos = [];
      for (const it of items) {
        let photos = it.photos || [];
        if (it.files?.length) {
          const urls = await uploadPhotos(it.files);
          photos = [...photos, ...urls];
        }
        withPhotos.push({ title: it.title, serviceType: it.serviceType, description: it.description, photos });
      }

      await axios.post('/api/requests/account', {
        propertyId: w.property._id,
        unitId: w.unitId || undefined,
        items: withPhotos,
        priority: w.priority || 'routine',
        nteAmount: w.nteAmount === '' ? undefined : w.nteAmount,
        access: w.access || {},
        documents: w.documents || [],
        requestedVendorId: w.requestedVendorId || undefined,
      }, authHeaders());

      setWoForm(null);
      await loadAccountData();
      showToast(w.priority === 'emergency' ? t('acct.emergencySent') : t('acct.workOrderSent'));
      setAccountView('jobs');
      goTo('screen-account-dashboard');
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  // The company raising the work order themselves, once they have approved the
  // price. The server holds them to that order — an instruction to do the work
  // over an estimate nobody said yes to is work done at an unauthorised number.
  const [isRaisingAccountWO, setIsRaisingAccountWO] = useState(false);
  const raiseAccountWorkOrder = async (request) => {
    setIsRaisingAccountWO(true);
    try {
      const res = await axios.post(`/api/requests/${request._id}/work-order`, {}, authHeaders());
      setActiveRequest(res.data);
      setRequests(prev => prev.map(r => (r._id === res.data._id ? { ...r, ...res.data } : r)));
      showToast(`${res.data.workOrder?.number}`);
      setPrintDoc({ request: res.data, kind: 'work-order' });
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally { setIsRaisingAccountWO(false); }
  };

  const openPropertyImport = () => { setFormError(''); setImportState({}); goTo('screen-property-import'); };
  const closePropertyImport = () => { setImportState(null); goTo('screen-account-dashboard'); };

  const previewImport = async (text, fileName, mapping) => {
    setFormBusy(true); setFormError('');
    try {
      const res = await axios.post('/api/properties/import/preview', { text, mapping }, authHeaders());
      // The raw text is kept because commit re-parses server-side rather than
      // trusting a parsed payload from here.
      setImportState({ text, fileName, preview: res.data });
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  const commitImport = async () => {
    const s = importState || {};
    setFormBusy(true); setFormError('');
    try {
      const res = await axios.post('/api/properties/import/commit',
        { text: s.text, mapping: s.preview?.mapping }, authHeaders());
      setImportState({ ...s, result: res.data });
      await loadProperties('');
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  // Refreshed every render, read only by the popstate listener above. This is
  // the list of things Back should close before it changes screen.
  backStack.current = {
    modal, setModal,
    vendorEditForm, setVendorEditForm,
    editingLeadItem, setEditingLeadItem,
    activeVendor, setActiveVendor,
    activeClient, setActiveClient,
    activeProject, setActiveProject,
    lastVendorLink, setLastVendorLink,
    closedByBack,
  };

  // How many layers are stacked over the screen right now, outermost first.
  // Each one needs a history entry of its own, or Back has nothing to consume
  // and the phone leaves the site instead — which is exactly what it used to do
  // with a half-filled sheet open.
  const overlayDepth =
    (activeVendor || activeClient || activeProject ? 1 : 0) +
    (modal || vendorEditForm || lastVendorLink ? 1 : 0) +
    (editingLeadItem !== null && editingLeadItem !== undefined ? 1 : 0);

  useEffect(() => {
    const was = overlayDepthRef.current;
    overlayDepthRef.current = overlayDepth;

    if (overlayDepth > was) {
      for (let i = was; i < overlayDepth; i++) {
        window.history.pushState({ screen: screenRef.current, overlay: i + 1 }, '', window.location.pathname);
      }
    } else if (overlayDepth < was) {
      // Closed with a Cancel or a Save rather than Back. The entries those
      // layers pushed are still in front of us, so wind them off — otherwise
      // Back would replay sheets the user has already finished with.
      if (closedByBack.current) closedByBack.current = false;
      else window.history.go(-(was - overlayDepth));
    }
  }, [overlayDepth]);

  // Everything the screens can read/call. Grouped for readability.
  const ctx = {
    // navigation + session
    currentScreen, goTo, currentUser, logout,
    // auth form
    authForm, setAuthForm, authError, handleAuthInputChange, handleAuthSubmit,
    // service request form
    formData, handleInputChange, isSubmitting, handleSubmit,
    requestItems, setRequestItems, newRequestItem, editingRequestItem, setEditingRequestItem,
    // data
    requests, vendors, clients, activeRequest, setActiveRequest, markRequestSeen,
    // dashboards
    activeTab, setActiveTab, adminView, setAdminView, homeownerTab, setHomeownerTab, vendorTab, setVendorTab,
    activeVendor, setActiveVendor, vendorLayout, setVendorLayout,
    handleToggleWatch, handleMoveVendorTrade,
    vendorEditForm, setVendorEditForm, openVendorEdit, openVendorAdd, handleSaveVendorEdit, isSavingVendor,
    lastVendorLink, setLastVendorLink,
    // estimate approval (homeowner)
    handleApproveEstimate,
    // modals
    modal, setModal,
    estimateForm, setEstimateForm, estimateTotal, handleSubmitEstimate,
    noteEdit, setNoteEdit, handleSaveNote, isSavingNote,
    printDoc, setPrintDoc, openWorkOrder, isRaisingWorkOrder,
    newBuild,
    assignVendorId, setAssignVendorId, handleAssignVendor, handleAdminApproveEstimate, openEstimate,
    completionFiles, setCompletionFiles, completionNote, setCompletionNote, handleSubmitCompletion,
    invoiceForm, setInvoiceForm, invoiceTotal, handleSubmitInvoice,
    handleApproveInvoice,
    // admin: add lead + appointments
    addLeadForm, setAddLeadForm, handleAddLead, editingLeadItem, setEditingLeadItem, uploadPhotos,
    appointmentInput, setAppointmentInput, handleSetAppointment,
    // admin: add or correct a client by hand
    addClientForm, setAddClientForm, openAddClientForm, openClientForm, handleAddClient, isSavingClient,
    lastClientLink, setLastClientLink,
    // admin: a client's own page
    activeClient, setActiveClient, openLeadForClient,
    openEstimateForClient, estimateForClient, setEstimateForClient,
    clientInvoiceForm, setClientInvoiceForm,
    openClientInvoice, openInvoiceFromEstimate, handleSaveClientInvoice, isSavingInvoice, openInvoiceDoc,
    askDeleteEstimate, askDeleteInvoice,
    // admin delete-with-confirmation
    confirmDelete, setConfirmDelete, handleConfirmDelete,
    // change password
    passwordForm, setPasswordForm, handleChangePassword,
    emailForm, setEmailForm, handleChangeEmail,
    // forgot/reset password
    forgotEmail, setForgotEmail, forgotStatus, forgotFrom,
    openForgotPassword, handleForgotPassword,
    resetForm, setResetForm, isResetting, handleResetPassword,
    // marketing screen
    authHeaders, showToast,
    // technician partnership application
    vendorProfile, vendorAppForm, setVendorAppForm, vendorDocFiles, setVendorDocFiles,
    isSubmittingApp, handleSubmitVendorApp,
    // vendor ledger
    ledger, ledgerVendorFilter, setLedgerVendorFilter, vendorView, setVendorView,
    activeEntry, isSavingLedger,
    ledgerForm, setLedgerForm, ledgerDocFiles, setLedgerDocFiles, openLedgerForm,
    handleSaveLedgerEntry, handleDeleteLedgerEntry, handleExportLedger,
    paymentForm, setPaymentForm, paymentBlock, openRecordPayment, handleRecordPayment, handleDeletePayment,
    progressForm, setProgressForm, progressFiles, setProgressFiles, openProgress, handleAddProgress,
    // messages
    thread, threadFor, isLoadingThread, openThread,
    messageText, setMessageText, messageFiles, setMessageFiles,
    isSendingMessage, handleSendMessage, unreadCounts,
    // inbound-text diagnostics
    smsStatus, refreshSmsStatus, isLoadingSms,
    // push notifications
    pushOn, pushBusy, togglePush, sendTestPush, pushBlockedReason, isInstalled,
    // installing + offline
    isOffline, canInstall, handleInstall, installHint,
    // projects
    projects, activeProject, setActiveProject, estimates,
    projectForm, setProjectForm, openProjectForm, handleSaveProject, handleDeleteProject,
    lineForm, setLineForm, openProjectLine, handleSaveProjectLine, handleDeleteProjectLine,
    clientPayForm, setClientPayForm, openProjectPayment, handleSaveClientPayment, handleDeleteClientPayment,
    expenseForm, setExpenseForm, receiptFile, setReceiptFile,
    openProjectExpense, handleSaveExpense, handleDeleteProjectExpense,
    estimateForm2, setEstimateForm2, estimateItemsTotal, openEstimateForm,
    handleSubmitTechEstimate, handleApproveEstimateToProject, isSavingProject,
    // compliance documents
    credentialForm, setCredentialForm, credentialFile, setCredentialFile,
    openCredential, handleSaveCredential, handleDeleteCredential,
    // commercial accounts
    account, accountMembers, accountView, setAccountView,
    properties, propertyTotal, propertySearch, setPropertySearch, loadProperties,
    selectedProperty, propertyHistory, openProperty,
    propertyForm, setPropertyForm, openPropertyForm, closePropertyForm, saveProperty, removeProperty,
    unitForm, setUnitForm, openUnitForm, closeUnitForm, saveUnit, deleteUnit,
    memberForm, setMemberForm, openMemberForm, closeMemberForm, saveMember, removeMember,
    saveAccountSettings,
    woForm, setWoForm, openAccountWorkOrder, closeAccountWorkOrder, submitAccountWorkOrder,
    importState, setImportState, openPropertyImport, closePropertyImport, previewImport, commitImport,
    accountTechs, raiseAccountWorkOrder, isRaisingAccountWO,
    addPropertyDocuments, removePropertyDocument, addRequestDocuments, addWorkOrderDocuments,
    formBusy, formError,
    // admin's view of the commercial accounts
    adminAccounts, loadAdminAccounts, adminAccountDetail, openAdminAccount,
    accountForm, setAccountForm, openAccountForm, handleCreateAccount,
    accountMemberForm, setAccountMemberForm, openAccountMemberForm,
    handleSaveAccountMember, handleRemoveAccountMember,
    adminPropertyForm, setAdminPropertyForm, openAdminPropertyForm, handleCreateAdminProperty,
    adminPropertyId, setAdminPropertyId,
    addAdminPropertyDocuments, removeAdminPropertyDocument, addJobDocuments,
    propertyJobForm, setPropertyJobForm, openPropertyJobForm, handleCreatePropertyJob,
    openAdminInvoice,
    lastInviteLink, setLastInviteLink,
    // Shared by the commercial forms: the same uploader and service-type list
    // the public request form and Julian's Add-a-Lead already use.
    uploadFiles: uploadPhotos, serviceTypes: SERVICE_TYPES,
    handleLogout: logout,
    openRequestDetail: openRequestDetailScreen,
    // toast
    toast,
  };

  return (
    <AppContext.Provider value={ctx}>
      <div className="App">
        <Home />
        <RequestForm />
        <LoginChoice />
        <HomeownerLogin />
        <VendorLogin />
        <AdminLogin />
        <AccountLogin />
        <ForgotPassword />
        <ResetPassword />
        <HomeownerDashboard />
        <RequestDetail />
        <EstimateReview />
        <AdminDashboard />
        <VendorDashboard />
        <AccountDashboard />
        <PropertyDetail />
        <PropertyForm />
        <UnitForm />
        <MemberForm />
        <AccountWorkOrderForm />
        <PropertyImport />
        <PrivacyPolicy />
        <Terms />
        <Modals />
        {/* The work order / estimate as a sheet of paper. Rendered above
            everything because printing takes the whole page. */}
        {printDoc && (
          <PrintDoc
            request={printDoc.request} invoice={printDoc.invoice} kind={printDoc.kind}
            onClose={() => setPrintDoc(null)}
            // Only Julian takes a work order back; a manager can raise one on
            // their own job but cancelling it is a dispatch decision.
            onDeleteWorkOrder={currentUser?.role === 'admin'
              ? (req) => { setPrintDoc(null); setConfirmDelete({ type: 'work order', id: req._id, label: req.workOrder?.number }); }
              : undefined}
            // The More menu. Admin only: these act on the record, not the view.
            onCreateWorkOrder={currentUser?.role === 'admin' ? (req) => openWorkOrder(req) : undefined}
            onDuplicate={currentUser?.role === 'admin'
              ? (printDoc.kind === 'invoice' ? duplicateInvoice : duplicateEstimate)
              : undefined}
            onDelete={currentUser?.role === 'admin'
              ? (printDoc.kind === 'invoice' ? askDeleteInvoice : askDeleteEstimate)
              : undefined}
          />
        )}
        <Toast />
        <OfflineBar />
        <UpdateBar />
      </div>
    </AppContext.Provider>
  );
}

export default App;
